import { AlertTypeEnum } from '@/models/Alert';
import { ActivityCorrectionStatusEnum } from '@/models/ActivityProgress';
import { GenerateDifficultyEnum } from '@/models/astro.ai/generate/Activity';
import { StageTypeEnum } from '@/models/Hackathon';
import { KlassStatusEnum } from '@/models/Klass';
import { ContentGivenEnum, PresenceEnum } from '@/models/ScheduledLessonReport';
import { UserTypeEnum } from '@/models/User';
import {
  GameErrorCodes,
  GroundToken,
  InteractiveToken,
} from '@cna-br/astro-code';
import { StudentTypeEnum } from '@/enums/StudentTypeEnum';
import TranslateInterface, {
  Sheet,
  TranslateBehaviorAssessmentForm,
  TranslateCommentAssessmentForm,
} from './translateInterface';
import { CourseDurationMinuteEnum } from '@/models/Course';
import { CourseLineCategory, CourseLineSubCategory } from '@/models/CourseLine';
import { ProgressStatusEnum } from '@/enums/ProgressStatusEnum';
import { KlassPeriodEnum } from '@/utils/klasses/klassPeriods';

const userStatus = {
  REGISTRED: 'Registred',
  ACTIVE_ENROLLMENT: 'Active Enrollment',
  INACTIVE: 'Inactive',
  WAITING_ENROLLMENT: 'Waiting Enrollment',
};

const klassStatus: Record<KlassStatusEnum, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  CONCLUDED: 'Concluded',
  CANCELED: 'Canceled',
};

const presenceStatus: Record<PresenceEnum, string> = {
  ATTENDED: 'Attended',
  MISSED: 'Missed',
  NONE: 'No data',
  RESCHEDULED: 'Rescheduled',
};

const activityCorrectionStatus: Record<ActivityCorrectionStatusEnum, string> = {
  NOT_SUBMITTED: 'Not submitted',
  PENDING: 'To correct',
  CORRECTED: 'Corrected',
};

const sheet: Sheet = {
  title: 'Title',
  comment: 'Comment',
  assessment: 'Assessment',
  assessments: {
    noInfo: 'No information added about',
    loadMore: 'Load more',
    moreDetails: 'More details',
  },
  occurrence: 'Occurrence',
};

const behaviorAssessmentForm: TranslateBehaviorAssessmentForm = {
  successCreatedMessage: 'Assessment created successfully',
  successUpdatedMessage: 'Assessment edited successfully',
  successDeletedMessage: 'Assessment deleted successfully',
  labels: {
    energy: 'Energy',
    maturity: 'Maturity',
    speed: 'Speed',
    attention: 'Attention',
    organization: 'Organization',
    initiative: 'Interest/Initiative',
    socialization: 'Socialization',
    apprenticeship: 'Apprenticeship',
  },
};

const commentAssessmentForm: TranslateCommentAssessmentForm = {
  successCreatedMessage: 'Comment created successfully',
  successUpdatedMessage: 'Comment edited successfully',
  commentInput: {
    label: {
      studentLabel: 'Add comment',
      klassLabel: 'Add observation',
    },

    placeholder: {
      studentCommentAbout:
        'Write a comment about the student. The information will be for internal use only and will not be viewed by students or guardians',
      klassCommentAbout:
        'Write a note about the class {{name}} , the information added will be for internal use and will not be viewed by the students or guardians',
    },
  },
  klassInput: {
    placeholder: 'Select a class',
  },
};

const occurrenceAssessmentForm = {
  successCreatedMessage: 'Occurrence created successfully',
  successUpdatedMessage: 'Occurrence edited successfully',
  successDeletedMessage: 'Occurrence deleted successfully',
  descriptionTextArea: {
    label: {
      student: 'Add occurrence',
    },
    placeholder: {
      student:
        'Write an observation about the incident with the student. The information will be for internal use only and will not be viewed by students or guardians',
    },
  },
  notificableCheckbox: {
    label: {
      student: 'Notify parents (notifies the secretary)',
    },
    info: {
      student:
        'If this option is selected, the incident will appear on the notification board, if you need to contact those responsible for the student',
    },
  },
};

const courseFormats = {
  ONLINE: 'Online',
  IN_PERSON: 'In person',
  SCHOOL: 'In school',
  EXTRA_COURSE: 'Extra course',
  SELF_PACED: 'Self-paced',
};

const businessModels = {
  CNA_STANDARD: 'CNA Standard',
  IN_SCHOOL: 'In School',
  SELF_PACED: 'Self-paced',
};

const courseType = {
  CTRL_KIDS: 'Ctrl+Kids',
  CTRL_TEENS: 'Ctrl+Teens',
  CTRL_YOUNG: 'Ctrl+Young',
  CTRL_QUICK: 'Ctrl+Quick',
  CTRL_PRO: 'Ctrl+Pro',
};

const courseLineCategory: Record<CourseLineCategory, string> = {
  [CourseLineCategory.LANGUAGE]: 'Language',
  [CourseLineCategory.TECHNOLOGY]: 'Technology',
  [CourseLineCategory.SCHOOL_REINFORCEMENT]: 'School Reinforcement',
};

const courseLineSubCategory: Record<CourseLineSubCategory, string> = {
  [CourseLineSubCategory.ENGLISH]: 'English',
  [CourseLineSubCategory.SPANISH]: 'Spanish',
  [CourseLineSubCategory.CTRL_PLAY]: 'Ctrl+Play',
};

const weekday = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

const studentType: {
  [key in StudentTypeEnum]: string;
} = {
  [StudentTypeEnum.REGULAR]: 'Regular',
  [StudentTypeEnum.SCHOOL]: 'School',
  [StudentTypeEnum.CONECTA_INTERNAL]: 'Conecta internal',
  [StudentTypeEnum.CONECTA_EXTERNAL]: 'Conecta external',
};

const en: TranslateInterface = {
  questionTypes: {
    CODE: 'Code',
    DND_POSITIONAL: 'Combination',
    DND_ORDENATION: 'Ordering',
    TRUE_OR_FALSE: 'True or False',
    QUIZ: 'Quiz',
    TEXT: 'Multimedia',
    CODE_EDITOR: 'Code Editor',
    BLOCK: 'Code Blocks',
    WRITING: 'Writing',
    FILL_IN_THE_BLANK: 'Fill in the blanks',
    FLASHCARD: 'Flashcard',
  },
  activityBlockTypes: {
    REGULAR: 'Regular',
    MANUAL_GRADE: 'Manual Grade',
    OPTIONAL: 'Optional',
    CONSOLIDATION: 'Consolidation',
  },
  examTypes: {
    TEST: 'Test',
    SIMULATED: 'Simulated',
  },
  termsOfUse: {
    title: 'TERMS OF USE AND SERVICES',
    toAccept: 'I accept',
    accepted: 'Terms accepted',
    readMore: 'Read more',
  },
  astrobot: {
    title: 'AstroBot',
    placeholder:
      'Type a question about the class, ask for examples, or tips to solve an activity...',
    thinking: 'Thinking',
    maximize: 'Maximize',
    minimize: 'Minimize',
    tokensLimit:
      'Your credits have run out, come back tomorrow to interact more',

    close: 'Close',
    help: 'Do you have any questions? I can help you',
    firstMessage:
      'Do you have any questions? I can help you with robotics and programming!',
    send: 'Send message',
    error: {
      message:
        'Sorry, an error occurred while processing your message: \n``` \n{{error}} \n``` \nPlease try again or contact support if the problem persists.',
    },
    quickPrompts: {
      actions: {
        summarize: 'Summarize this lesson',
        activities: 'Are there any activities?',
        keyPoints: 'What are the key points?',
      },
    },
  },
  blockCodeRuntime: {
    blocks_one: 'Block',
    blocks_other: 'Blocks',
  },
  errors: {
    serverError: 'Internal Error, please try again later or contact support',
    networkError:
      'Oops, it seems you are offline. Check if your internet connection is stable.',
    invalidQuestionAnswer:
      'Your answer could not be saved because it is in an invalid format',
    errorPage: {
      backButton: 'Back',
      panelButton: 'Panel',
      reloadButton: 'Reload',
      notFound: {
        title: 'Oops! This page does not exist',
        subtitle: 'The page you are looking for was not found',
      },
      offline: {
        title: 'Oops, it looks like you are offline.',
        subtitle: 'Please check if your internet connection is stable.',
      },
      activityNotFound: {
        title: 'Oops! This activity does not exist',
        subtitle:
          'The activity you are looking for was not found, please check that the url is correct',
      },
      internError: {
        title: 'Oops! Something went wrong',
        subtitle: "We'll fix the problem, please try again later.",
      },
      courseNotFound: {
        title: 'Oops! This course does not exist.',
        subtitle:
          'The course you are looking for was not found, please check that the url is correct.',
      },
      materialNotFound: {
        title: 'Oops! This material does not exist',
        subtitle:
          'The activity you are looking for was not found, please check that the url is correct',
      },
      forbidden: {
        title: 'Oops! You do not have permission to access this page',
        subtitle:
          'You do not have permission to access this page, please contact the administrator',
      },
      badRequest: {
        title: "Oops! something didn't work as expected",
        subtitle: "We'll fix the problem, please try again later.",
      },
      lessonForbidden: {
        title: 'Oops! You do not have permission to access this lesson',
        subtitle:
          'Make sure you are enrolled in this course or contact an administrator',
      },
      reactException: {
        title: 'Oops! It is rare but it happens!',
        subtitle:
          'Try reloading the page, if the error persists, we will solve it as soon as possible!',
      },
    },
    errorPrefix: 'Fix the following errors: ',
  },
  formValidation: {
    user: {
      username: {
        pattern: 'Just letters, numbers and . - _ are accepted for username',
        minLength: 'Usernane must have at least 5 charactere',
        usernameExists: 'Username already exists',
        startsLetters: 'Username can start only with letters',
        required: 'Username is required',
      },
    },
    general: {
      url: {
        pattern: 'Enter a valid url',
      },
    },
  },
  common: {
    all: 'All',
    tryAgain: 'Try again',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    performanceValue: '{{value}}%',
    resultsRange: 'Results: {{first}}-{{last}} of {{total}}',
    embedPreview: {
      visitProject: 'Visit project',
    },
    dndPlaceholder: 'Drag and drop items to this list',
    forbiddenAction: 'You do not have permission to perform this action',
    objectives: 'Objectives',
    next: 'Next',
    correct: 'Correct',
    incorrect: 'Incorrect',
    previous: 'Previous',
    back: 'back',
    title: 'Title',
    content: 'Content',
    acceptPermited: 'File type not allowed. We only accept {{accept}}',
    fileSizeLimit: 'The file exceeds the size limit. The limit is {{limit}}',
    maxFilesLimitReached_one: 'Limit of {{count}} file reached',
    maxFilesLimitReached_other: 'Limit of {{count}} files reached',
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
    periodOfTheDay: 'Period of the day',
    course: 'Course',
    lessons: 'Lessons',
    newest: 'newest',
    oldest: 'oldest',
    delete: 'Delete',
    emptyMessage: 'No results',

    you: 'You',
    difficulty: {
      [GenerateDifficultyEnum.ADVANCED]: 'Advanced',
      [GenerateDifficultyEnum.BEGINNER]: 'Beginner',
      [GenerateDifficultyEnum.INTERMEDIATE]: 'Intermediate',
    },
    saved: 'Saved',
    clear: 'Clear',
    close: 'Close',
    dateRangeInput: {
      selectDate: 'select dates',
      cancelSelect: 'cancel selection',
    },
    complexDateInput: {
      placeholder: 'Select',
      close: 'Close',
      divider: 'or pick from the calendar',
      clear: 'Clear date',
      directions: {
        past: 'Last',
        next: 'Next',
        this: 'This',
      },
      granularities: {
        day: 'Day(s)',
        week: 'Week(s)',
        month: 'Month(s)',
        year: 'Year(s)',
      },
      granularitiesThis: {
        day: 'Today',
        week: 'This Week',
        month: 'This Month',
        year: 'This Year',
      },
      directionAriaLabel: 'Period direction',
      amountAriaLabel: 'Amount',
      granularityAriaLabel: 'Time unit',
    },
    timeInput: {
      placeholder: 'Select time',
      clear: 'Clear time',
      hours: 'Hours',
      minutes: 'Minutes',
    },
    scheduledLessonCard: {
      time: '{{time}}h',
      endsAt: 'Ends: {{date}}',
      students_one: '{{count}} student',
      students_other: '{{count}} students',
      attendances_one: '{{count}} attendance',
      attendances_other: '{{count}} attendances',
      exams_one: '{{count}} exam',
      exams_other: '{{count}} exams',
      requiresAttention: 'Requires attention',
    },
    copied: 'Copied',
    copy: 'Copy',
    collapse: 'Collapse',
    tool: 'Tools',
    expand: 'Expand',
    moreOptions: 'More options',
    rename: 'Rename',
    newVersion: 'New Version',
    discardVersion: 'Discard Version',
    backToVersioning: 'Back to versioning',
    preview: 'Preview',
    scheduledLessonReportStatus: {
      ACTIVE: 'Active report',
      CANCELED: 'Canceled report',
      TRANSFERED: 'Transfered report',
      NOT_ENROLLED: 'Not active in this lesson',
    },
    userType: {
      [UserTypeEnum.SUPER_ADMIN]: 'Super Admin',
      [UserTypeEnum.UNIT_ADMIN]: 'Admin',
      [UserTypeEnum.TEACHER]: 'Teacher',
      [UserTypeEnum.PARENT]: 'Parent',
      [UserTypeEnum.STUDENT]: 'Student',
      [UserTypeEnum.CONTENT_CREATOR]: 'Content Creator',
    },
    studentAge: {
      sixOrLess: '6 or less',
      sevenToNine: '7 to 9',
      tenToThirteen: '10 to 13',
      fourteenToSeveteen: '14 to 17',
    },
    madeBy: '© Made by',
    lessonUnpublished: 'Lesson unpublished',
    lessonNotAssigned: 'Lesson not assigned',
    withLove: 'with',
    inBrazil: 'in 🇧🇷',
    userNotFound: 'User not found',
    courses: 'Courses',
    add: 'Add',
    filter: {
      admin: 'Admin',
      teacher: 'Teacher',
      clearButton: 'Clear',
      okButton: 'OK',
    },
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },

    day: 'Day',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    never: 'Never',
    generalInformation: 'General information',
    alterPassword: 'Alter password',
    rewards: 'Rewards',
    lastLogin: 'Last login',
    orderBy: 'Order by',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    teacherPrefix: 'Teacher',
    form: {
      required: 'This field {{field}} is required',
      empty: 'The field {{field}} cannot be empty',
      invalid: 'The field {{field}} is invalid',
      startDate: {
        title: 'Start date',
      },
      endDate: {
        title: 'End date',
      },
      reason: {
        title: 'Motive',
        placeholder: 'Enter a motive for vacation',
      },
      recess: {
        title: 'Recess dates',
        validation: 'Cannot remove all dates',
      },
      title: {
        title: 'Title',
      },
      subtitle: {
        title: 'Subtitle',
      },
      language: {
        title: 'Language',
      },
      content: {
        title: 'Content',
      },
      newPassword: { title: 'New password' },
      reNewPassword: { title: 'Re new password' },
    },
    completed: 'Completed',
    lessonTitle: 'Lesson title',
    edit: 'Edit',
    notEditable: 'Not editable',

    input: {
      unit: {
        label: 'Unit',
        placeholder: 'Select a Unit',
      },
      teacher: {
        label: 'Teacher',
        placeholder: 'Select a teacher',
      },
    },
    students: 'Students',
    info: 'Informations',
    sheet: 'Sheet',
    noResults: 'No results',
    students_one: '{{count}} student',
    students_other: '{{count}} students',
    increasingTime: 'Increasing time',
    descendingTime: 'Descending time',

    accessibility: {
      yourClassShip: 'Your class ship',
      weekdayKlassCell: 'Class cell',
      klassPopup: 'Class popup',
      todayLessons: 'Today lessons',
      notificationsPage: 'Notifications page',
      vacationNotice: 'Vacation notice',
    },

    onVacation: 'On vacation',
    dates: 'Dates',
    notifications: 'Notifications',
    toAdd: 'To add',
    warnings: 'Warnings',
    noInfo: 'No information added about {{fullName}}',
    loadMore: 'Load more',
    remove: 'Remove',
    noDates: 'No dates',
    optional: 'Optional',

    lessonTabs: {
      details: 'Info',
      material: 'Student Material',
      teacherMaterial: 'Teacher Material',
      activity: 'Activity',
      challenge: 'Challenge',
      resource: 'Resource',
    },

    newResource: 'New resource',
    fileInstructions: 'Drag or click to add a file',
    finish: 'Finish',
    unknown: 'Unknown',
    file: 'File',
    revert: 'Revert',
    deleted: 'Deleted',
    activity: 'Activity',
    link: 'Link',
    open: 'Open',
    download: 'Download',
    activities: 'Activities',
    start: 'Start',
    template: 'Template',
    grade: 'Grade',
    notFoundFile: 'No files provided.',
    resources: 'Resources',
    seeAnswers: 'See answers',
    see: 'See',
    mandatory: 'Mandatory',
    unit: 'Unit',
    teacher: 'Teacher',
    user: 'User',
    courseType: 'Course type',
    klass: 'Class',
    role: 'Role',

    select: 'Select',
    standard: 'Default',
    otherFilters: 'Other filters',
    loading: 'Loading',
    modality: 'Modality',
    startSoon: 'Start soon',
    endSoon: 'End soon',
    hasActiveLessons: 'Has active lessons',
    klassType: 'Type of class',
    first: 'First',
    termination: 'Termination',
    status: 'Status',
    actions: 'Actions',
    ascending: 'Ascending',
    descending: 'Descending',
    research: 'Research',
    doNotAnticipateLesson: 'It is not possible to anticipate this class',
    classStartsSoon: 'Class starts soon',
    lesson: 'Lesson {{order}}',
    unassignedLesson: 'No lesson',
    unitWithOrder: 'Unit {{order}}',
    refresh: 'Refresh',
    hideSection: 'Hide section',
    today: 'Today',
    pending: 'Pending',
    dayPeriod: {
      am: 'AM',
      pm: 'PM',
    },
    occurrenceDescription: '{{name}}: "{{description}}"',
    name: 'Name',
    email: 'Email',
    enrollmentType: 'Enrollment type',
    newInstructions: 'New instructions',
    instructions: 'Instructions',
    allSectionsAdd: 'All sections have now been added',
    noInstructions: 'No instructions',
    homework: 'Homework',
    matrix: 'Matrix',
    availableSoon: 'Available soon',
  },
  fileUpload: {
    selectFile: 'Select file',
    selectAnotherFile: 'Select another file',
    dropToUpload: 'Drop the file to upload',
    unsupportedFormatTitle: 'Unsupported format',
    unsupportedFormatDescription:
      '“{{name}}” is not an accepted format. Upload a {{accept}} file.',
    fileTooLargeTitle: 'File larger than {{max}}',
    fileTooLargeDescription:
      '“{{name}}” is {{size}}. Reduce its size and try again.',
    uploading: 'Uploading... {{loaded}} of {{total}} · {{percent}}%',
  },
  imageInput: {
    chooseImage: 'Choose an image',
    delete: 'Delete',
  },
  imageZoom: {
    expand: 'Expand image',
    close: 'Close expanded image',
  },
  login: {
    sendEmail: {
      forgotPassword: 'Forgot my password',
      emailLabel: 'E-mail',
      weRecoverPassword: "We'll recover your password",
      back: 'Back',
      emailSent: 'Email successfully sent',
      emailPlaceholder: 'Type your e-mail',
      usernamePlaceholder: 'Type your username',
      submit: 'Submit',
      checkEmail:
        'Check your email for instructions to finalize your password reset',
      specifyUser: 'Specify user to change password',
      userTypeRequired: 'Unspecified user type',
      usernameRequired: 'Username is required',
    },
    resetPassword: {
      alreadyRegister: 'Already have an account?',
      confirmNewPassword: 'Confirm the new password',
      confirmRedefine: 'Reset password',
      login: 'Login here',
      typeNewPassword: 'Type your new password',
      newPassword: 'New password',
      redefinePassword: 'Reset your password',
      differentPasswordError: 'The two passwords do not match!',
      modal: {
        canLogin: 'You already can login using your new password',
        success: 'Your password was reseted successfuly',
      },
    },
    motivationalMessage: 'Ready to start learning? 🚀',
    loginAccount: 'Log in with your account',
    login: 'Login',
    loginPlaceholder: 'Type your e-mail or username',
    password: 'password',
    passwordPlaceholder: 'Type your password',
    keepConnected: 'Keep me connected',
    signIn: 'Sign In',
    forgotPassword: 'Forgot your password?',
  },

  newAccount: {
    questionText: 'New around here?',
    linkText: 'Create a free account',
  },

  tableHeader: {
    search: 'Search',
    filter: 'Filter',
    increasing: 'Increasing',
    decreasing: 'Decreasing',
  },

  modalAddStudent: {
    addStudentPhrase: 'Which students would you like to add?',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },

  confirmModal: {
    defaultTitle: 'Are you sure?',
    confirmDestructive: 'Delete',
    confirmDefault: 'Confirm',
    cancel: 'Cancel',
  },

  modalChangePassword: {
    passwordChangeMessage:
      'Are you sure you want to generate a new password for this student?',
    confirm: 'Confirm',
    cancel: 'Cancel',

    warning: 'Warning!',
    lostChangesWarning:
      'You have unsaved changes that will be lost if you leave the page',
    confirmLeave: 'Are you sure you want to leave?',
    leave: 'Leave',
    stay: 'Stay',

    passwordChangeSuccess: 'New password generated successfully!',
    emailSent:
      "The new password has been sent to the student's and guardian's email",
    close: 'Close',
  },
  modalGenerateMatrix: {
    title: 'Instructions',
    subtitle:
      'Below, you can provide additional instructions for matrix generation',
    generate: 'Generate',
    hasValue: 'The current matrix will be replaced',
    form: {
      materialType: {
        label: 'Material format:',
        options: {
          APRESENTATION: 'Slides',
          TEXT: 'Text',
        },
      },
      instructions: {
        label: 'Instructions',
        placeholder: 'Enter relevant information about the course matrix',
      },
    },
  },
  modalGenerateObjective: {
    title: 'Generate Objectives',
    subtitle:
      'Define the topic and purpose to generate personalized learning objectives',
    generate: 'Generate Objectives',
    hasValue: 'The current objectives will be replaced',
    form: {
      topic: {
        label: 'Topic',
        placeholder: 'Ex: Python Programming, Digital Marketing, etc.',
      },
      purpose: {
        label: 'Purpose',
        placeholder:
          'Ex: Enable students for web development, prepare for certification, etc.',
      },
    },
  },
  modalGenerateQuestion: {
    title: 'Select a question type and parameters',
    generate: 'Generate',
    form: {
      numberOfAlternatives: 'Number of alternatives',
      topic: {
        label: 'Topic',
        placeholder: 'Type a topic',
      },
      difficulty: { label: 'Difficulty', placeholder: 'Select' },
    },
  },
  modalWarning: {
    modalUpdateWinner: {
      attention: 'Attention!',
      warning: 'The project below is already in this ranking',
      proceed: 'Do you want to replace it with the current project?',
      confirm: 'Replace',
      cancel: 'Cancel',
    },
    modalUpdateCourse: {
      attention: 'Attention!',
      warning:
        'By updating to the new course version, the class will have 1h30min of duration. If the duration of the classes was longer before, please consider the duration in the signed contracts and make sure the responsible teachers are aware. All students will be affected and their progress will be reset.',
      proceed: 'Do you want to update to the new course version?',
      confirm: 'Update',
      cancel: 'Cancel',
    },
    modalAnswerKey: {
      attention: 'Attention!',
      warning:
        'If you open the answers key, you will not be able to redo the activity anymore',
      proceed: 'Do you want to see the answer key now?',
      confirm: 'See Answers',
      cancel: 'Cancel',
    },
    gerateChargesModal: {
      attention: 'Attention!',
      cancel: 'Cancel',
      confirm: 'Confirm',
      proceed: 'Are you sure you want to continue?',
      warning:
        'Upon confirmation that the student was present in the class, <strong>a charge will be generated</strong> for the unit of origin.',
    },
    modalLeaveActivityPage: {
      attention: 'Attention!',
      warning: 'If you leave the activity, your progress will not be saved',
      proceed: 'Are you sure you want to quit?',
      confirm: 'Leave',
      cancel: 'Stay',
    },

    modalSaveChanges: {
      attention: 'Are you sure you want to change it?',
      warning: 'This action will record the information',
      proceed: '',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },

    modalDiscardChanges: {
      attention: 'Are you sure you want to cancel the changes?',
      warning: 'This action will not save the information',
      proceed: '',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },

    modalSaveHomework: {
      attention: 'Attention!',
      warning: 'The assignment will be published to all students',
      proceed: 'Are you sure you want to publish it?',
      confirm: 'Publish',
      cancel: 'Cancel',
    },

    modalDeleteChapter: {
      attention: 'Attention!',
      warning: 'Are you sure you want to delete this chapter?',
      proceed: 'This action cannot be undone.',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },

    lessonReportModal: {
      attention: 'Are you sure you want to mark the class as complete?',
      warning: '',
      proceed: 'You will not be able to undo this action.',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },

    modalNoComment: {
      attention: 'No comments were added for the student',
      warning: 'Are you sure you want to proceed?',
      proceed: '',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },

    modalChangeKlass: {
      attention: 'Changes made to these classes were not saved',
      cancel: 'Cancel',
      confirm: 'Confirm',
      warning:
        'Selecting a new class or leaving the page will cause you to lose your changes',

      proceed: 'Are you sure you want to proceed?',
    },
    modalRemoveVacationNotice: {
      attention: 'Are you sure you want to remove this vacation notice?',
      warning: 'You will not be able to undo this action',
      cancel: 'Cancel',
      confirm: 'Confirm',
      proceed: '',
    },
    modalPublishLesson: {
      attention: 'Attention!',
      warning:
        'By publishing this lesson for general use, it will be immediately available for use in courses and extra lessons',
      proceed: 'This action cannot be undone. Do you want to continue?',
      confirm: 'Publish',
      cancel: 'Cancel',
    },
  },

  modalDisable: {
    disable: 'Disable',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',

    modalDisableActivityElement: {
      warning: 'Are you sure you want to disable this question?',
      lostChangesWarning:
        'This action will not delete question information for students',
    },
    modalDisableHackaton: {
      warning: 'Are you sure you want to discard this hackathon?',
      lostChangesWarning:
        'Discarding the hackathon is an action that cannot be undone.',
    },
    modalDisableHackatonStage: {
      warning: 'Are you sure you want to discard this hackathon stage?',
      lostChangesWarning:
        'Discarding the hackathon stage is an action that cannot be undone.',
    },

    modalDeleteCourse: {
      warning: 'Are you sure you want to discard this course?',

      lostChangesWarning:
        "Discard course is an action that can't be undone. All saved and unsaved changes will be lost.",
    },

    modalDisableChallenge: {
      warning: 'Are you sure you want to disable this challenge?',
      lostChangesWarning:
        'This action will not delete student progress or information',
    },

    modalDisableUnit: {
      warning: 'Are you sure you want to disable this unit?',
      lostChangesWarning: 'This action will preserve the informations',
    },

    modalDisableKlass: {
      warning: 'Are you sure you want to deactivate this class?',
      lostChangesWarning:
        'This action will not cause students to lose their progress',
    },

    modalDisableLesson: {
      warning: 'Are you sure you want to delete this lesson?',
      lostChangesWarning:
        'This action will not delete student progress or information',
    },
    modalDeleteLesson: {
      warning: 'Are you sure you want to delete this lesson?',
      lostChangesWarning:
        'This action will delete all changes done in this lesson',
    },

    modalDisableTeacher: {
      warning: 'Are you sure you want to disable this teacher?',
      lostChangesWarning: 'This action will preserve the informations',
    },

    modalDisableActivity: {
      warning: 'Are you sure you want to disable this activity?',
      lostChangesWarning:
        'This action will not delete student progress or information',
    },

    modalRemoveBook: {
      warning: 'Are you sure you want to remove the material?',
      lostChangesWarning:
        'This action cannot be undone and all information will be lost.',
    },

    modalStudentAbsence: {
      warning: `Are you sure you want to remove this student's attendance?`,
      lostChangesWarning:
        'This action will also remove the entire class report, including grades and annotations if they have already been filled in.',
    },

    modalDisableScheduledLesson: {
      warning: 'Are you sure you want to delete this lesson?',
      lostChangesWarning: 'This will change all scheduled lessons',
    },
    modalDisableTopic: {
      warning: 'Are you sure you want to delete this topic?',
      lostChangesWarning: 'This action may affect students progress',
    },
    modalDisableDifficulty: {
      warning: 'Are you sure you want to delete this difficulty?',
      lostChangesWarning: 'This action may affect students progress',
    },
    orderLessons: {
      lostChangesWarning:
        'Students will already receive this update and will be able to see that the order has changed.',
      warning: 'Are you sure you want to make this change?',
    },
    deleteProject: {
      warning: 'Are you sure you want to delete the project?',
      lostChangesWarning: 'This action cannot be undone',
    },
    deleteResource: {
      warning: 'Are you sure you want to delete the resource?',
      lostChangesWarning: 'This action cannot be undone',
    },
    deleteResourceActivity: {
      warning: 'Are you sure you want to delete this activity?',
      lostChangesWarning:
        'This action will not delete student progress or information',
    },

    deleteSectionInstructions: {
      warning: 'Are you sure you ant to delete this instruction?',
      lostChangesWarning: 'This action cannot be undone',
    },

    deleteChapter: {
      warning: 'Are you sure you want to delete this chapter?',
      lostChangesWarning: 'This action cannot be undone',
    },

    deleteLessonBlock: {
      warning: 'Are you sure you want to delete this lesson block?',
      lostChangesWarning:
        'There are lessons in this block that will be lost and cannot be recovered after saving.',
    },
  },

  avatar: {
    profile: 'Profile',
  },

  suggestionsPage: {
    suggestions: 'Suggestions',
    seeAll: 'See all',
    subheading:
      'Here you can suggest or vote for new features, as well as see their development progress',
    updates: 'Latest News',
  },

  countdown: {
    materialButton: 'Material',
    classroomButton: 'Classroom',
    nextClass: 'NEXT CLASS',
    days: 'days',
    hours: 'hours',
    minutes: 'minutes',
    seconds: 'seconds',
    accessLesson: 'Access Lesson',
    watchVideo: 'Watch Video',
    instructionUrlText:
      'This class has a video on how to install and/or use the program. Watch before your class using the button below:',
    materialText:
      'Before your class starts you can already access the material and read about it in the button below:',
    lessonText:
      'Before your class starts you can already access its content clicking in the button below:',
    accessClassroom: 'Classroom',
    classroomTooltip:
      'The Classroom is available 30 minutes before the class starts',
  },
  sideMenu: {
    home: 'Home',
    classroom: 'My Lessons',
    courses: 'Courses',
    extraCourses: 'Extra Courses',
    feedback: 'Feedback',
    achievements: 'Achievements',
    notifications: 'Notification',
    admin: 'Admin',
    howWorks: 'How it Works',
    expand: 'Expand',
    collapse: 'Minimize',
    calendar: 'Calendar',
    config: 'Config',
    leave: 'Logout',
    teacher: 'Teacher',
    projects: 'Projects',
  },
  courses: {
    status: {
      EDITING: 'Editing',
      VERSIONING: 'Versioning',
      BETA: 'Beta',
      PUBLISHED: 'Published',
      NEW: 'Draft',
      DISABLED: 'Deactivate',
    },
    legalInformation: 'Legal Information',
  },
  coursePathForm: {
    codeWillOverride:
      'You are in limited edition mode. Any changes made will overwrite the previous version as soon as this version is published.',
    isbnCode: 'Insert the ISBN code or other legal information',
    saveSuccessfuly: 'Changes saved successfully',
  },
  activityPage: {
    verifyAnswer: 'Check answer',
    sendAnswer: 'Send answer',
    finishActivity: 'Send answer and finish',
    answeredAt: 'Last modified on {{date}}',
    questionPosition: '{{current}} of {{total}}',
    selectActivity: 'Select below the activity you want to access',
    activityAttemptFinished: 'Activity successfully completed',
    goodJob: 'Good job!',
    continue: 'Continue',
    activityCompleted:
      'Congratulations! You have completed the activity block “{{blockName}}”. This is a big step in your learning journey. Keep it up!',
    excellent: 'Excellent!',
    correctOnAttempt: 'Nice! You got it right on attempt {{attempt}}',
    correctAnswerCongrats: 'The answer is correct, congratulations!',
    xpEarnedOnAttempt: 'You earned {{xp}} XP.',
    attemptNumber: 'Attempt {{attempt}}',
    incorrectAnswer: 'The answer is incorrect, try again.',
    translate: 'Translate',
    undoTranslate: 'Undo translation',
    advance: 'Next',
    skip: 'Skip',
    tryAgain: 'Try again',
    showCorrectAnswer: 'Reveal Answer',
    correctAnswer: 'Correct answer',
    correct: 'Correct',
    incorrect: 'Incorrect',
    pending: 'Pending',
    writingActivity: {
      title: 'Sent to the teacher',
      body:
        'As soon as the teacher grades this activity, we will let you know.',
      editAnswer: 'Edit answer',
    },
  },
  klassPage: {
    nextKlass: 'Next Lesson',
    myLessons: 'My Lessons',
    myLessonsMessage:
      'Here you can find all the lessons available in your module',
    myLessonsCompleted: "There's no lessons to do!",
    errorMessage: {
      lessonListPhrase: 'Unable to load class list',
      nextLessonPhrase: 'Unable to load next class',
    },
  },
  lesson: {
    availableSoon: 'This lesson will be available soon!',
    availableExplain:
      'This lesson is not yet available. You will be able to access it one week before the date of the lesson, or if your teacher changes the order of the lessons',
    backLessons: 'See lessons',

    teacher: {
      activity: {
        answerError: 'An error occurred while saving the response',
        errorTextSubTitle: 'There was an error opening the activity',
        errorTextTitle: 'Activity not found',
        klass: {
          submittions: 'Submittions',
          errorTextTitle: 'Class not found',
          errorTextSubTitle: 'There was an error fetching the class',
          noStudents: 'This class has no students',
        },
      },
    },
    manage: {
      selectKlass: 'Select a klass',
      bankLesson: {
        title: 'Lesson Bank',
        details: 'Add a lesson from the lesson bank',
      },
      addLesson: {
        cantAdd: 'Unable to add lesson between two lessons that passed',
        title: 'Add Lesson',
        details: 'Add a free or regular lesson in course.',
      },
      editLesson: {
        title: 'Edit Lessons',
        details: 'Change content or order of course lessons.',
      },
      invalidLessonsMessage:
        'There are invalid lessons in this class. Please notify support.',
    },
    access: {
      accessVideo: 'Access video',
      accessProgram: 'Access program',
      accessAndInstallation: 'Access and Installation',
      hasVideoInstallationText:
        'This class has a video on how to install and/or use the program.',
      clickToWatchVideoInstalationText:
        'Click on the buttons below to watch the video on how to use the program and to access it.',
    },
    attention: 'Attention!',
    closeActivity: {
      buttonCancel: 'Cancel',
      buttonConfirm: 'Close',
      warning: 'If you close activity your progress will not be saved',
      confirmMessage: 'Are you sure want to close activity?',
    },
    activityProgress: 'Activity',
    controlButton: {
      bookNext: 'CONTINUE',
      bookPrev: 'BACK',
      activityNext: 'NEXT',
      activityPrev: 'PREVIOUS',
      finish: 'FINISH',
      unexpectedError:
        'An unexpected error has occurred. Please try reloading the page or contact support.',
    },
    activity: {
      false: 'F',
      true: 'T',
      titleHeader: 'Activities',
      start: 'Start',
      view: 'View',
      score: 'Score',
      notDone: 'Not done',
      feedback: 'Feedback',
      redo: 'Redo',
      homework: 'Homework',
      noQuestions: 'No questions in this activity',
      noQuestionsSubtitle:
        'This activity has no questions, either add questions to it or remove it',
      givenByTeacher: 'given by teacher',
      activityNext: 'NEXT',
      activityPrev: 'PREVIOUS',
      notFoundTitle: 'Activity not found',
      notFoundSubtitle: 'Fail quering activity',
      result: {
        finished: 'Finished',
        resultText: 'Result',
        congratulationsText:
          'Congratulations, you have completed the activity! 🎉',
        questions: 'Questions',
        correct: 'Correct',
        percentage: 'Percentage',
        review: 'Review',
        detailReview: 'Review the questions and their answers',
        remake: 'Remake',
        detailRemake: 'Retry the activity',
        leave: 'Leave',
      },
      card: 'Activity',
      text: {
        instruction: 'Write your answer in the field below',
        correction: "Teacher's correction",
        grade: 'Grade',
      },
      task: {
        instruction: 'Upload the file in the field below',
        upload: 'Click here and select a file to upload',
      },
      quiz: {
        statement: 'Choose the correct option',
        correctAnswer: 'Correct answer',
      },
      trueOrFalse: {
        correctAnswer: 'Correct answer',
        question: 'True or false?',
      },
      dragAndDropOrdenation: {
        instruction: 'Arrange the items in the correct order',
        errorReordenation: 'Error when trying to reorder items',
        wrongAnswer: 'Correct order',
      },
      dragAndDropPositional: {
        instruction:
          'Arrange the items on the right to match the correct order on the left',
        errorReordenation: 'Error when trying to reorder items',
      },
      video: {},
    },
  },
  detachedLessonEditPage: {
    publishForSelectedUnits: 'Publish for selected units',
    publishForGeneralUse: 'Publish for general use',
  },
  modalBank: {
    manage: 'Manage',
    disabledButton: 'Unavailable',
    inUseLesson: 'lesson present in the course',
    inUseActivity: 'in the course',
    private: 'Private',
    public: 'Public',
    edit: 'Edit',
    expansibleContent: {
      lesson: {
        description: 'Description',
        links: 'Links',
      },
      activity: {
        name: 'Name',
        viewActivities: 'View questions',
      },
    },
    openModal: {
      activities: 'Activity Bank',
      questions: 'Question Bank',
      lessons: 'Lesson Bank',
      all: 'Content Bank',
    },
    tooltip: {
      insideBank: 'Inside the bank',
      outsideBank: 'Outside the bank',
    },
    list: {
      empty: {
        lessons: 'No lesson found',
        activities: 'No activity found',
        questions: 'No question found',
        all: 'No item found',
      },
    },
    selectTypeButton: {
      activities: 'Activities',
      questions: 'Questions',
      lessons: 'Lessons',
      all: 'All',
    },
    section: {
      types: 'Types',
      filters: 'Filters',
    },
    filters: {
      author: 'Author',
      unit: 'Unit',
      tool: 'Tool',
      my: 'my authorship',
      course: 'my course',
      search: 'Search',
    },
    authorFilter: {
      pedagogical: 'Pedagogical',
      teacher: 'Teacher',
      my: 'My',
      all: 'All',
    },
    useButton: {
      activities: 'Use Activity',
      questions: 'Use Question',
      lessons: 'Use Lesson',
      all: 'Use Content',
      addingActivity: 'Adding activity...',
    },
  },
  accountPage: {
    soundSettings: 'Sound Settings',
    alertSound: 'Alert Sound',
    accountTitle: 'My Configurations',
    accountSubheading: 'You can change your account information here',
    accountInfoTitle: 'Account Information',
    accountPasswordTitle: 'Account Password',
    personalInfoTitle: 'Personal Information',
    accountSystemTitle: 'System Configuration',
    theme: 'Theme',
    alterTimezone: 'Timezone',
    personalInfo: 'Personal',
    accountInfo: 'Account',
    systemInfo: 'System',
    fieldName: 'Name',
    fieldLastname: 'Lastname',
    fieldEmail: 'E-mail',
    fieldUsername: 'Username',
    fieldPhoneNumber: 'Phone Number',
    fieldDateOfBirth: 'Date of Birth',
    fieldAddress: 'City, State',
    fieldState: 'State',
    fieldCity: 'City',
    selectCity: 'Type the city',
    currentPassword: 'Current password',
    saveErrorDifferentPassword: 'The two passwords do not match!',
    fieldNewPassword: 'New password',
    fieldConfirmPassword: 'Confirm new password',
    saveButton: 'Save',
    editButton: 'Edit',
    cancelButton: 'Cancel',
    saveSuccess: 'Saved successfully',
    saveError:
      'Something went wrong, make sure the current password is correct',
    visualSettings: 'Visual Settings',
    changeConsent: 'Change consent information',
  },
  klass: {
    selectKlass: 'Select a class',
    lessons: {
      inBank: 'Lesson Bank',
      readOnly: 'Not editable',
      success: 'Changes saved successfully',
      cantOrder: 'Unable to order this lesson',
      errorTextTitle: 'There was an error loading lessons',
      errorTextSubTitle: 'Please contact support',
      finish: 'Finish',
      cancel: 'Cancel',
      view: 'View',
      title: 'Edit Class Course',
      details: 'Information',
      activity: 'Activity',
      teacherBook: 'Teacher Material',
      studentBook: 'Student Material',
      challenge: 'Challenge',
      modal: {
        cancel: {
          success: 'Changes discarded successfully',
        },
      },
      lessonDetail: {
        errorTool: 'Invalid url',
        deleteSuccess: 'Lesson deleted successfully',
        ImageUploadSuccess: 'The image was successfully uploaded:',
        ImageUploadError_1: 'Warning: The image upload',
        ImageUploadError_2: 'failed! Try again!',
        normal: 'Normal',
        free: 'Free',
        lessonTitle: 'Lesson Title',
        toolUrl: 'Add a program access link',
        instructionsUrl: 'Add a video link on how to use the program',
        addLink: 'Add Link',
        save: 'Save',
        exclude: 'Delete Lesson',
        freeExplain:
          'When the content of the class is outside the curriculum, increasing the total number of classes in the course. Recommended for events or holidays.',
        normalExplain:
          'When you need to replace a tool or spend more time finishing a project. You can disable another class to keep the total of 18 classes.',
      },
      alert: {
        saved: 'Saved',
      },
    },
  },
  studentHistoric: {
    title: 'Student History',
    enrollTransfer: 'Enrollment transferred on class',
    enrollCanceled: 'Enrollment canceled on class',
    enrolled: 'Enrolled on class',
    noData: 'No data about this student',
    fullScreen: 'full screen',
  },
  adminPage: {
    title: 'Admin',
    teacherTitle: 'Teacher',
    noTeachers: 'No teacher found in this unit',
    noAdminScreen: "The admin screen isn't available for mobile devices",
    useComputer: 'Please use a computer',
    editCourseRules: {
      cantModify: 'Not allowed in limited editing',
      activityWarning:
        'You are in limited edition courses so changes such as: editing answers or create/deleting questions are not allowed',
    },
    createCourse: 'Create Course',
    addStudent: 'Add Student',
    createTeacher: 'Create Teacher',
    createClass: 'Create Class',
    students: 'Students',
    teachers: 'Teachers',
    topics: 'Topics',
    prompts: 'AI Prompts',
    classes: 'Classes',
    trial: 'Trial Lessons',
    courses: 'Courses',
    paths: 'Paths',
    units: 'Units',
    hackathons: 'Hackathons',

    calendars: 'Calendars',
    recesses: 'Recesses',
    team: 'Team',
    lesson: {
      modal: {
        beta: {
          title: 'Warning!',
          warning:
            'The course will be available to teachers see e and make sugestions.',
          question: 'Are you sure you want finish editing?',
          cancel: 'Cancel',
          confirm: 'Finish',
          success: 'The course has been successfully added to beta',
        },
        cancel: {
          title: 'Are you sure you want to discard this edit?',
          warning:
            'Cancel all changes is an action cannot be reverted. All saved and unsaved changes will be lost',
          question: 'Are you sure you want to cancel all changes?',
          cancel: 'Back',
          confirm: 'Discard',
          success: 'Changes canceled successfully',
        },
        publishEdit: {
          title: 'Warning!',
          warning:
            'Changes will be published to all students immediately. Review well the changes before published them',
          question: 'Do want to publish changes?',
          cancel: 'Cancelar',
          confirm: 'Publish',
          success: 'Course published successfully',
        },
        publishBeta: {
          title: 'Warning',
          warning:
            'The course will be available to new classes. Review and be sure all is right before publish course',
          question: 'Are you sure you want to publish the course?',
          cancel: 'Cancel',
          confirm: 'Publish',
          success: 'Changes published successfully',
        },
        enableEdit: {
          title: 'Warning!',
          warning:
            'When enable edit, the course will not be available to teachers view and will turn be a draft',
          question: 'Are you sure you want to enable edit?',
          cancel: 'Cancel',
          confirm: 'Enable',
        },
      },
      successPublish: 'Course updated successfully',
      editingTitle: 'Course Editing',
      draftTitle: 'Course in Draft',
      versioningTitle: 'New Course Version',
      see: 'View',
      finish: 'Finish',
      editLessons: 'Edit Lessons',
      publish: 'Publish',
      discard: 'Discard',
      editingWarning:
        'You are in limited edition courses only for minor corrections or changes. Published changes will be displayed to students who are already enrolled in courses. To make more changes, select the "New Version" option.',
      versioningWarning:
        'The new course version will not available to students who are already enrolled in the course. Only new students will see the changes',
      newLesson: 'New Lesson',
      successDeleteMessage: 'Class successfully deleted',
      errorDeleteMessage: 'There was an error deleting the class',
      successOrderMessage: 'Lessons reordered successfully',
      errorOrderMessage: 'There was an error reordering the lessons',
      successCreateMessage: 'Class created successfully',
      errorCreateMessage: 'There was an error creating the class',
      modalDeleteConfirm: 'Are you sure you want to delete this class?',
      modalWarning: 'This action cannot be undone',
      errorNotValidExtension: 'The file must be of type .zip',
      lessonTabs: {
        details: 'Info',
        material: 'Student Material',
        teacherMaterial: 'Teacher Material',
        activity: 'Activity',
        challenge: 'Challenge',
        resource: 'Resource',
      },
      lessonDetails: {
        title: 'Lesson Title:',
        description: 'Lesson Description:',
        videoInstallMessage:
          'If this class has a video on how to install/access the program for the class, enter the video:',
        videoId: 'Video Url:',
        bannerUpload: 'Select an image for the lesson',
        deleteLesson: 'Delete Lesson',
        saveLesson: 'Save',
        programLinkMessage:
          'If the program has an access address, enter it below:',
        programUrl: 'Program access address:',
        programLinkExample: 'Example: https://www.google.com.br/',
        motivation: 'Motivation message:',
        success: 'Lesson saved successfully',
        error: 'An error occurred while trying to save the lesson',
      },
      addMaterial: {
        materialTitle: 'Material Title',
        teacherMaterialTitle: 'Teacher Material Title',
        addMaterial: 'Add Material',
        tryAgain: 'Try again',
        verifyBook: 'Verify the integrity of the file or contact support',
        takingTooLong:
          'It is taking too long to format the chapters. Please wait or try again.',
        selectMaterial: 'Select course material',
        waitComplete: 'The chapters are being formatted. Please wait.',
        errorMessageUploadZip: 'Attention: material upload failed! Try again!',
        successDeleteMaterial: 'Material successfully deleted',
        errorDeleteMaterial:
          'There was an error when trying to delete the Book',
        successUploadZip: 'The material was successfully uploaded !',
        errorSaveZip: 'An error occurred while saving the material',
        addButton: 'Upload',
        modalConfirm: 'Are you sure you want to delete this book?',
        modalWarning: 'This action cannot be undone',
        back: 'Back',
        slide: 'in Slides',
        text: 'in Text',
        materialType: 'Material Type',
      },
      alert: {
        saved: 'Saved',
      },
    },
    manageUnit: {
      units: 'Units',
      title: 'Unit Management',
      addUnit: 'ADD UNIT',
      totalUnit: 'TOTAL UNITS',
      timezone: 'Timezone',
      successAddUnitMessage: 'Unit added successfully',
      errorAddUnitMessage: 'There was an error adding the drive',
      successEditUnitMessage: 'Unit edited successfully',
      errorEditUnitMessage: 'An error occurred while editing the unit',
      name: 'Name',
      nameErrorMessage: 'Enter the unit name',
      address: 'Address',
      neighbourhood: 'Neighbourhood',
      addressErrorMessage: 'Enter an address',
      neighbourhoodErrorMessage: 'Enter a neighbourhood',
      addressNumber: 'Address number',
      addressNumberErrorMessage: 'Enter the unit address number',
      addressComplement: 'Complement',
      addressReference: 'Reference',
      city: 'City',
      cityErrorMessage: 'Enter the city of the unit',
      state: 'State',
      stateErrorMessage: 'Select unit state',
      zipcode: 'Zipcode',
      phone: 'Cell',
      cnpj: 'CNPJ',
      corporateName: 'Corporate Name',
      emailErrorMessage: 'Enter unit email',
      add: 'Add',
      save: 'Save',
      cancel: 'Cancel',
      status: 'Status',
      typeStatus: {
        active: 'Active',
        inactive: 'Inactive',
      },
    },
    listUnits: {
      errorTextSubTitle: 'Contact suport',
      errorTextTitle: 'Error listing units',
      recess: 'Recess',
      infoTooltip: 'General information',
      search: 'Search:',
      clearFilters: 'Clear Filters',
      clearOrder: 'Clear Orders',
      clearAll: 'Clear Orders and Filters',
      searchNotFound: 'Search not found',
      name: 'Unit Name',
      city: 'City',
      state: 'State',
      actions: 'Actions',
      editTooltip: 'Edit',
      deleteTooltip: 'Delete Unit',
      lastUpdate: 'Last update',
      modalConfirmMessage: 'Are you sure you want to delete this unit?',
      modalWarningMessage: 'This action cannot be undone.',
      successDeleteUnitMessage: 'Unit successfully deleted',
      errorDeleteUnitMessage: 'An error occurred while deleting the drive',
      teachers: 'Teachers',
      students: 'Students',
      classes: 'Classes',
      churn: 'Churn',
      presence: 'Presence',
      status: 'Status',
      units: 'Units',
    },
  },

  teacherCalendar: {
    lesson: 'Lesson',
    nextLesson: 'Next lesson:',
    teacherNoKlass: 'This teacher has no classes yet',
    endDate: 'End date:',
    students: 'Students:',
    noMoreLessons:
      'This class has no more lessons, update the status to inactive',
  },

  teacherPage: {
    title: 'Teacher',
    classes: 'Classes',
    scheduledKlasses: 'Lessons',
    students: 'Students',
    reports: 'Reports',
  },

  teacherComments: {
    successAdd: 'Comment added successfully',
    errorAdd: 'There was an error adding the comment',
    errorUpvote: 'There was an error upvoting the comment',
    successDelete: 'Comment deleted successfully',
    errorDelete: 'There was an error deleting the comment',
    at: 'at',
    comment: 'Comment',
    comments: 'Comments',
    reply: 'Reply',
    submit: 'Submit',
    response: 'Response',
    responses: 'Responses',
  },

  manageTrialLesson: {
    trialLessons: 'Trial Lessons',
    title: 'Manage Trial Lessons',
    add: 'Add Lesson',
    totalSeats: 'Total Seats',
    seatsPerTeacher: 'Seats per Teacher',
    deleteTooltip:
      'This lesson already has students, change the enrollment first',
    filledSeats: 'Filled Seats',
    noSchedule: 'This unit has no available lessons',
    deleteSuccess: 'Date deleted successfully',
    deleteError: 'There was an error deleting this date',
    attendance: 'Attendance',
    addLesson: {
      canotChangeTeacher: 'You have already added classes for this teacher',
      teacher: 'Teacher',
      dates: 'Dates',
      seats: 'Seats',
      units: 'Unit',
      notTrialLesson: 'There are no trial classes scheduled',
      successMessage: 'Trial lessons created successfully',
    },
  },

  manageCourse: {
    courses: 'Courses - Admin',
    title: 'Courses Management',
    teachersQuantity: 'TOTAL TEACHERS',
    studentsPerTeacher: 'STUDENTS PER TEACHER',
    weeklyAccesses: 'WEEKLY ACCESSES',
    add: 'ADD COURSE',
    coursesQuantity: 'TOTAL COURSES',
    klassesTotal: 'TOTAL CLASSES',
    studentsTotal: 'TOTAL STUDENTS',
    addCourse: {
      name: 'Course name',
      slug: 'Course slug',
      abbreviation: 'Course abbreviation',
      type: 'Course type',
      category: 'Category',
      f2f: 'In person',
      online: 'Online',
      isActive: 'Course is Active?',
      duration: 'Course Duration',
      dragger: 'Select a image for the course',
      addButton: 'Add',
      saveButton: 'Save',
      addSuccess: 'Course created successfully',
      addError: 'There was an error creating the course',
      editSuccess: 'Changes saved successfully',
      editError: 'An error occurred while trying to save the changes',
      ImageUploadSuccess: 'The image was successfully uploaded:',
      ImageUploadError_1: 'Warning: The image upload',
      ImageUploadError_2: 'upload failed! Try again!',
      ImageTypeError: 'Banner image must be one of this file types:',
      slugPopover:
        'The short name is needed to create the course URL(link), Example: Ctrl Kids 2 = ctrl-kids-2',
      errorMessageName: 'Insert the course name!',
      errorMessageSlug: 'Insert the course slug',
      errorMessageAbbreviation: 'Insert the course abbreviation!',
      errorMessageType: 'Select the course type!',
      errorMessageDuration: 'Insert the course duration!',
      cancelButton: 'Cancel',
      Quick: 'QUICK',
      typeSelect: 'Select course type',
    },
    editCourse: {
      format: 'Format',
      abbreviation: 'Course abbreviation',
      duration: 'Duration',
      courseLine: 'Course Line',
      courseModule: 'Module',
      isActive: 'Status',
      name: 'Course name',
      type: 'Course type',
      errorMessageAbbreviation: 'Insert the course abbreviation!',
      errorMessageName: 'Insert the course name!',
      saveButton: 'Save',
      errorMessageDuration: 'Insert the course duration!',
      cancelButton: 'Cancel',
      dragger: 'Select a image for the course',
      published: 'Published',
      disabled: 'Disabled',
      editing: 'Editing',
      versioning: 'Versioning',
      editError: 'There was an error editing the course',
      editSuccess: 'Course edited successfully',
      ImageUploadSuccess: 'The image was successfully uploaded:',
      ImageUploadError_1: 'Warning: The image upload',
      ImageUploadError_2: 'upload failed! Try again!',
      active: 'Active',
      inactive: 'Inactive',
      Quick: 'Quick',
      editButton: 'Edit',
      info: 'Info',
      saveCreated: 'Course created successfully',
      saveEdited: 'Course edited successfully',
      wanings: {
        emptyField: 'Fill in this field',
        noChanges: 'No changes identified',
      },
      errors: {
        name: {
          maxLength: 'The name must not exceed 200 characters',
        },
        abbreviation: {
          maxLength: 'The abbreviation must not exceed 7 characters',
          exists: 'This abbreviation already exists',
        },
      },
    },
    dataCourseTable: {
      editTooltip: 'Edit Course',
      deleteTooltip: 'Delete Course',
      modalConfirm: 'Are you sure you want to delete this course?',
      modalWarning:
        'All progress of this course will be excluded and this action cannot be undone',
      deleteSuccess: 'Course successfully deleted',
      deleteFail: 'There was an error when trying to delete the course',
      branchTooltip: 'New Version',
      infoTooltip: 'Info',
      viewTooltip: 'View Course',
      statisticNotFound: 'N/A',
      Quick: 'QUICK',
      searchNotFound: 'Search not found',
      noCourses: 'There are no registered courses',
      noCoursePath:
        "It's not possible to edit this course. Please contact support",
      school: 'School',
    },
    listCourses: {
      search: 'Search:',
      clearFilters: 'Clear Filters',
      clearOrder: 'Clear Order',
      clearAll: 'Clear Order and Filters',
      name: 'Course',
      type: 'Type',
      students: 'Students',
      status: 'Status',
      actions: 'Actions',
      category: 'Category',
      churn: 'Churn',
      presence: 'Presence',
      version: 'Version',
    },
  },
  manageStudent: {
    students: 'Students',
    title: 'Student Management',
    age_other: '{{count}} years old',
    age_one: '{{count}} year old',
    accesses: 'ACCESSES',
    studentWarning:
      'Attention! It is not possible to make edits to students through the Portal, only through the Store',
    weeklyAccesses: 'WEEKLY ACCESSES',
    quantity: 'STUDENTS',
    add: 'ADD STUDENT',
    back: 'back',
    listUsers: {
      search: 'Search:',
      clearFilters: 'Clear Filters',
      clearOrder: 'Clear Order',
      clearAll: 'Clear Order and Filters',
      name: 'Name',
      email: 'E-mail',
      unit: 'Unit',
      klass: 'Class',
      phoneNumber: 'Phone Number',
      role: 'Role',
      lastAccess: 'Last Access',
      status: 'Status',
      actions: 'Actions',
    },
    dataUserTable: {
      passwordTooltip: 'Change Password',
      infoTooltip: 'Info',
      never: 'Never',
      searchNotFound: 'Search not found',
      noStudents: 'There are no registered students',
      rewardsTooltip: 'Rewards',
      pedagogicalSheetTooltip: 'Pedagogical Sheet',
      historicTooltip: 'Student Historic',
      vacationNoticeTooltip: 'Vacation notice',
    },
    status: userStatus,
    addUser: {
      info: 'Info',
      edit: 'Edit',
      unit: 'Unit',
      name: 'Name',
      lastName: 'Last name',
      selectCity: 'Type a city',
      email: 'Email',
      username: 'Username',
      password: 'Password',
      usernameRule: 'Only letters, dot, hyphen e underline',
      passwordConfirm: 'Password confirm',
      errorMessagePasswordMatch:
        'The two passwords that you entered do not match!',
      birth: 'Birth',
      state: 'State',
      city: 'City',
      bio: 'Bio',
      klass: 'Class',
      phoneNumber: 'Phone Number',
      role: 'Role',
      status: 'Active',
      Registred: 'Registred',
      Active_Enrollment: 'Active Enrollment',
      Inactive: 'Inactive',
      Waiting_Enrollment: 'Waiting Enrollment',
      errorMessageUnit: 'Select the Unit',
      errorMessageName: 'Enter student name',
      errorMessageLastName: "Enter student's last name",
      errorMessageEmail: 'Enter student email',
      errorMessageUsername: 'Enter student user',
      errorMessagePassword: 'Enter student password',
      errorMessagePasswordConfirm: 'Confirm student password',
      errorMessageBirth: "Enter student's date of birth",
      errorMessageState: 'Enter student state',
      errorMessageCity: 'Enter student city',
      errorMessageKlass: 'Select a class for the student',
      addButton: 'Add Student',
      successCreateUser: 'Student created successfully',
      errorCreateUser: 'There was an error creating the student',
      successEditUser: 'Changes saved successfully',
      errorEditUser: 'An error occurred while trying to save the changes',
      saveButton: 'Save',
      cancelButton: 'Cancel',
      addUserRuleOne:
        'just letters, numbers and . - _ are accepted for username or email',
      addUserRuleTwo: 'fill all fields',
      addUserRuleThree: 'make sure the password fields match',
    },
  },

  feedbackPage: {
    pageTitle: 'Lesson Feedback',
    context: {
      students: 'Students',
      teachers: 'Teachers',
    },
    all: 'all',
    byCourse: 'by course',
    byModule: 'by module',
    byKlass: 'by class',
    byLesson: 'by lesson',
    header: {
      average: 'Average',
      lessonRating: 'Lesson content',
      teacherRating: `Teacher's explanation`,
      notFound: 'No feedback found',
      closeFeedback: 'Close feedback',
      loadFeedback: 'Load feedback',
      oldest: 'oldest',
      all: 'All',
      newest: 'newest',
      positive: 'Positive 😍',
      neutral: 'Neutral 😐',
      negative: 'Negative 😖',
    },
    filter: {
      courseLine: 'Course Line',
      module: 'Module',
      selectCourse: 'Select a course first',
      units: 'Units',
      rating: 'Rating',
      teacher: 'Teacher',
      klass: 'Class',
    },
    tag: { feedbackSingular: 'feedback', feedbackPlural: 'feedbacks' },
    noLessonsFound: 'No lessons found',
  },

  manageTeam: {
    team: 'Team',
    title: 'Team Management',
    course: {
      label: 'Courses',
      placeholder: 'Select a course',
    },
    accesses: 'ACCESSES',
    teacherWarning:
      'Attention! It is not possible to create teachers or make some edits through the Portal, only on Loja',
    weeklyAccesses: 'WEEKLY ACCESSES',
    quantity: 'TEAM',
    add: 'ADD STUDENT',
    back: 'back',
    SUPER_ADMIN: 'Super Admin',
    CONTENT_CREATOR: 'Content Creator',
    UNIT_ADMIN: 'Admin',
    TEACHER: 'Teacher',
    status: userStatus,
    newUser: 'New user',
    fromUnit: 'From another unit',
    selectUser: 'Select user',
    selectUnit: 'Select unit',
    unitLabel:
      'The user will retain any previous units and add the new one selected below:',
    errorUserAlreadyHasUnit: 'User already in this unit',
    listUsers: {
      search: 'Search:',
      clearFilters: 'Clear Filters',
      clearOrder: 'Clear Order',
      clearAll: 'Clear Order and Filters',
      name: 'Name',
      email: 'E-mail',
      unit: 'Unit',
      klass: 'Klass',
      phoneNumber: 'Phone Number',
      role: 'Role',
      lastAccess: 'Last Access',
      status: 'Status',
      actions: 'Actions',
    },
    dataUserTable: {
      searchNotFound: 'Search not found',
      passwordTooltip: 'Change Password',
      infoTooltip: 'Info',
      never: 'Never',
    },
    addUser: {
      info: 'Info',
      edit: 'Edit',
      unit: 'Unit',
      name: 'Name',
      lastName: 'Last name',
      selectCity: 'Type a city',
      selectRole: 'Select a role',
      email: 'Email',
      username: 'Username',
      password: 'Password',
      usernameRule: 'Only letters, dot, hyphen e underline',
      passwordConfirm: 'Password confirm',
      errorMessagePasswordMatch: 'The two passwords do not match!',
      birth: 'Birth',
      state: 'State',
      city: 'City',
      bio: 'Bio',
      klass: 'Klass',
      phoneNumber: 'Phone Number',
      role: 'Role',
      status: 'Active',
      Registred: 'Registred',
      Active_Enrollment: 'Active Enrollment',
      Inactive: 'Inactive',
      Waiting_Enrollment: 'Waiting Enrollment',
      errorMessageUnit: 'Select the Unit',
      errorMessageName: 'Enter team member name',
      errorMessageLastName: "Enter team member's last name",
      errorMessageEmail: 'Enter team member email',
      errorMessageUsername: 'Enter team member user',
      errorMessagePassword: 'Enter team member password',
      errorMessagePasswordConfirm: 'Confirm team member password',
      errorMessageBirth: "Enter team member's date of birth",
      errorMessageState: 'Enter team member state',
      errorMessageCity: 'Enter team member city',
      errorMessageKlass: 'Select a class for the team member',
      addButton: 'Add team member',
      successCreateUser: 'Teammate created successfully',
      errorCreateUser: 'There was an error creating the team member',
      successEditUser: 'Changes saved successfully',
      errorEditUser: 'An error occurred while trying to save the changes',
      saveButton: 'Save',
      cancelButton: 'Cancel',
      addUserRuleOne:
        'just letters, numbers and . - _ are accepted for username',
      addUserRuleTwo: 'fill all fields',
      addUserRuleThree: 'make sure the password fields match',
    },
  },
  game: {
    edit: {
      title: 'Edit map',
      resize: { rows: 'Rows', cols: 'Columns' },
      allEnemiesShouldMove: 'All enemies should move',
    },
    layers: {
      ground: 'Ground layer',
      interactive: 'Interactive layer',
    },
    selectBlocks: {
      interactive: {
        title: 'Items',
        description: 'Add or remove items that the student must interact with',
      },
      ground: {
        title: 'Block',
        description: 'Add or remove blocks that the student must pass through',
      },
    },
    validators: {
      [GameErrorCodes.DUPLICATE_SPRITE]:
        'You cannot add two items with the same sprite',
      [GameErrorCodes.EXISTS_INTERACTIVE_ITEM_IN_COORD]:
        'There is already an interactive item in this coordinate',
      [GameErrorCodes.INVALID_COLLECTIBLE_POSITION]:
        'The collectible item must be in a grass block',
      [GameErrorCodes.INVALID_ENEMY_POSITION]:
        'The enemy cannot be in a limit block',
      [GameErrorCodes.INVALID_MIN_MATRIX_DIMENSIONS]:
        'The matrix must be at least 3x3',
      [GameErrorCodes.INVALID_MAX_MATRIX_DIMENSIONS]:
        'The matrix must be at most 13x13',
      [GameErrorCodes.INVALID_GROUND_POSITION]:
        'Invalid position for ground block',
      [GameErrorCodes.GROUND_SHOULD_CONTAIN_ALL_INTERACTIVE_ITEMS]:
        'The ground must contain all interactive items',
      [GameErrorCodes.PLAYER_SIDE_ENEMY]:
        'An enemy that moves cannot be next to the player',
      [GameErrorCodes.INVALID_INITIAL_POSITION_PLAYER]:
        'Invalid initial position for the character',
      [GameErrorCodes.REGULAR_MATRIX]:
        'The matrix must be regular, all lines must have the same size',
      [GameErrorCodes.WORKSPACE_UNDEFINED]: 'The workspace is not defined',
    },
    tokens: {
      names: {
        [GroundToken.FINAL]: 'Podium',
        [GroundToken.REGULAR]: 'Grass',
        [GroundToken.DANGER]: 'Lava',
        [GroundToken.LIMIT]: 'Limits',
        [InteractiveToken.PLAYER]: 'Character',
        [InteractiveToken.COLLECTIBLE]: 'Collectible',
        [InteractiveToken.ENEMY]: 'Enemy',
        [InteractiveToken.DIRECTION]: 'Player direction indicator',
      },
      describes: {
        [GroundToken.DANGER]: 'Forbidden path (the student cannot pass here)',
        [GroundToken.REGULAR]: 'Free path (the student can pass here)',
        [GroundToken.FINAL]: 'Arrival point (the student must arrive here)',
        [GroundToken.LIMIT]: 'Ground block that indicates the map limit',
        [InteractiveToken.PLAYER]: 'set where the character should start',
        [InteractiveToken.COLLECTIBLE]:
          'Add or remove an item that the student must collect on the map',
        [InteractiveToken.ENEMY]:
          'Add or remove an enemy that the student must avoid',
        [InteractiveToken.DIRECTION]: 'Player direction indicator',
      },
    },
  },
  manageTeacher: {
    teachers: 'Teachers',
    title: 'Student Management',
    accesses: 'ACCESSES',
    weeklyAccesses: 'WEEKLY ACCESSES',
    quantity: 'TEACHERS',
    add: 'ADD TEACHER',
    back: 'back',
    status: userStatus,
    listUsers: {
      search: 'Search:',
      clearFilters: 'Clear Filters',
      clearOrder: 'Clear Order',
      clearAll: 'Clear Order and Filters',
      name: 'Name',
      email: 'E-mail',
      unit: 'Unit',
      klass: 'Class',
      phoneNumber: 'Phone Number',
      role: 'Role',
      lastAccess: 'Last Access',
      status: 'Status',
      actions: 'Actions',
      students: 'Students',
      presence: 'Presence',
      churn: 'Churn',
    },
    dataUserTable: {
      noTeachers: 'There are no registered teachers',
      searchNotFound: 'Search not found',
      passwordTooltip: 'Change Password',
      editTooltip: 'Edit Teacher',
      deleteTooltip: 'Delete Teacher',
      never: 'Never',
      IntoTooltip: 'General information',
      studentsTooltip: 'Students from in progress classes',
      lessonsTooltip: 'Classes',
      noProfileFound: 'Teacher profile not found',
    },
    addUser: {
      info: 'Info',
      edit: 'Edit',
      unit: 'Unit',
      name: 'Name',
      lessons: 'Lessons',
      filters: {
        allLessons: 'all lessons',
        completedLessons: 'completed lessons',
        plannedLessons: 'planned lessons',
      },
      noContent: {
        allLessons: 'No class the teacher',
        completedLessons: 'No class completed by teacher',
        plannedLessons: 'No classes scheduled for this teacher',
      },
      lastName: 'Last name',
      email: 'Email',
      username: 'Username',
      usernameRule: 'Only letters, dot, hyphen e underline',
      password: 'Password',
      passwordConfirm: 'Password confirm',
      errorMessagePasswordMatch:
        'The two passwords that you entered do not match!',
      birth: 'Birth',
      state: 'State',
      city: 'City',
      selectCity: 'Type a city',
      bio: 'Bio',
      klass: 'Class',
      phoneNumber: 'Phone Number',
      role: 'Role',
      status: 'Status',
      Registred: 'Active',
      Active_Enrollment: 'Active Enrollment',
      Inactive: 'Inactive',
      Waiting_Enrollment: 'Waiting Enrollment',
      errorMessageUnit: 'Select the Unit',
      errorMessageName: 'Enter teacher name',
      errorMessageLastName: "Enter teacher's last name",
      errorMessageEmail: 'Enter teacher email',
      errorMessageUsername: 'Enter teacher user',
      errorMessagePassword: 'Enter teacher password',
      errorMessagePasswordConfirm: 'Confirm teacher password',
      errorMessageBirth: "Enter teacher's date of birth",
      errorMessageState: 'Enter teacher state',
      errorMessageCity: 'Enter teacher city',
      errorMessageKlass: 'Select a class for the teacher',
      addButton: 'Add teacher',
      successCreateUser: 'Teacher created successfully',
      errorCreateUser: 'There was an error creating the student',
      successEditUser: 'Changes saved successfully',
      errorEditUser: 'An error occurred while trying to save the changes',
      saveButton: 'Save',
      cancelButton: 'Cancel',
      addUserRuleOne:
        'just letters, numbers and . - _ are accepted for username or email',
      addUserRuleTwo: 'fill all fields',
      addUserRuleThree: 'make sure the password fields match',
      typeStatus: {
        active: 'Active',
        inactive: 'Inactive',
      },
    },
  },

  manageHackathon: {
    add: 'Add hackathon',
    title: 'Hackathon Management',
    hackathonStages: 'Stages',
    info: 'Information',
    noHackathons: 'No hackathons found',
    titles: {
      hackathon: 'Hackathon',
      theme: 'Theme',
      content: 'Content',
    },
  },
  manageKlasses: {
    totalStudents: 'TOTAL STUDENTS',
    status: klassStatus,
    klasses: 'Classes',
    title: 'Class Management',
    klassesPerTeacher: 'STUDENT PER CLASS',
    klassesQuantity: 'TOTAL CLASSES',
    studentsPerTeacher: 'STUDENTS PER TEACHER',
    weeklyAccesses: 'WEEKLY ACCESSES',
    add: 'ADD KLASS',
    updateCourse: {
      title: 'Update to new course version',
      success: 'Class updated successfully',
    },
    addKlass: {
      warning: 'Changes to the class can only be made on Loja',
      selectDayTimes: 'First select the day of the week and class time',
      info: 'Info',
      edit: 'Edit',
      course: 'Course',
      f2f: 'In person',
      classRoomUrl: 'Link to the online class',
      online: 'Online',
      startDate: 'Start',
      endDate: 'End',
      klassStatus,
      duration: 'Duration',
      status: 'Status',
      klassDateAndTime: 'Day and Time',
      recurrence: 'Recurrence',
      recessCalendar: 'Calendar',
      teacher: 'Teacher',
      students: 'Students',
      add: 'Add',
      save: 'Save',
      cancel: 'Cancel',
      addStudentButton: 'Add Students',
      addSuccess: 'CLass created successfully',
      addError: 'There was an error creating the class',
      saveSuccess: 'Changes saved successfully',
      saveError: 'An error occurred while trying to save the changes',
      moveTooltip: 'Move Student',
      deleteTooltip: 'Remove Student',
      unit: 'Unit',
      meetLink: 'Meet Link',
      dayOfWeek: 'Day of the week',
      time: 'Time',
      day: 'Day',
      error: {
        unit: 'Select the unit',
        course: 'Enter the course',
        klassDate: 'Enter the course start date',
        klassTime: 'Enter the class schedule',
        recurrence: 'Select class recurrence',
        recessCalendar: 'Select the Recess Calendar',
        teacher: 'Enter the class teacher',
        dayWeek: 'Enter the weekday',
        students: 'Enter at least one student',
        duration: 'Enter the class duration',
        invalidTime: 'Enter a valid time',
        klassType: 'Enter the class type',
        klassStatus: 'Enter the class status',
      },
      select: {
        course: 'Select the course',
        teacher: 'Select the teacher',
        duration: 'Select the class duration',
        day: 'Select the class weekday',
      },

      maxCapacity: 'Max capacity',
      conectaSeats: 'Conecta Seats',
      klassType: 'Class type',
      coursePathId: 'Course',
    },
    dataKlassTable: {
      noKlasses: 'There are no classes registered',
      searchNotFound: 'Search not found',
      infoTooltip: 'Info',
      editTooltip: 'Edit Class',
      processingEnrollment: 'Processing Enrollment',
      deleteTooltip: 'Delete Class',
      noDate: 'No date',
      teacherStudentsTooltip: 'Teacher and students',
      classProgressTooltip: 'Class progress',
      klassSheetTooltip: 'Class sheet',
      teacherStudentsPill: 'Teacher and students',
      lessonsTooltip: 'Scheduled lessons',
      modalConfirm: 'Are you sure you want to delete this class?',
      modalWarning:
        'All progress of this class will be excluded and this action cannot be undone',
      deleteSuccess: 'Class successfully deleted',
      deleteFail: 'There was an error when trying to delete the class',
      teacher: 'Teacher',
      students: 'Students',
      noStudentsToFollow: 'No students to follow',
      months: {
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December',
      },
      scheduledLessonProgressView: {
        klasses: 'Classes',
        everyKlass: 'every class',
        completedKlasses: 'completed classes',
        scheduledKlasses: 'scheduled classes',
        antecipate: {
          save: 'Start date changed',
          button: 'alter',
          start: 'Start:',
        },
        noScheduledLessons: 'No scheduled lessons for show',
        pendingKlasses: 'with pending reports',
        ascending: 'ascending',
        descending: 'descending',
        sort: 'Sort',
      },
    },
    listKlasses: {
      search: 'Search:',
      clearFilters: 'Clear Filters',
      clearOrder: 'Clear Order',
      clearAll: 'Clear Order and Filters',
      klass: 'Class',
      unit: 'Unit',
      start: 'Start',
      end: 'End',
      status: 'Status',
      actions: 'Actions',
      activeClasses: 'Active classes:',
    },
  },

  klassProgressPage: {
    lessonBlocked: 'This lesson is not released yet, it will be soon',
    pageTitle: 'Class Progress',
    notFoundTitle: "It looks like the class you're looking for doesn't exist",
    notFoundText: 'Please try another class',
    genericErrorTitle: "Oops, there's an issue",
    genericErrorText: 'Contact the support team or try again later',
    noStudents: 'This class has no student yet, please select another class',
    lessons: 'Lessons',
    lesson: 'Lesson',
    attendances: 'Attend.',
    noProgress: 'No progress for this lesson associated with this class',
    students: 'Students',
    absences: 'Absences',
    aboutKlass: 'About the class',
    progressAverage: 'Progress Average',
    averagePP: 'Average PP',
    pleaseFillReport: 'Please, fill up the lesson report',
    givenContent: 'Given Content',
    lessonScheduled: 'Lesson Scheduled',
    lessonIsScheduled: 'This lesson is scheduled for the next few days',
    reportNotFilled: 'Lesson report not filled in',
    seeReport: 'see report',
    missed: 'Missed',
    active: 'Active',
    inactive: 'Inactive',
    all: 'All',
    noInactiveStudent: 'No inactive student',
    noActiveStudent: 'No active student',
    transferred: 'Transferred',
    attended: 'Attended',
    pending: 'Pending',
    done: 'Done',
  },

  activity: {
    errorTextTitle: "Can't find activities",
    errorTextSubTitle: 'Verify if you are in correct page or contact support',
    newActivity: 'New Activity',
    textLabel: 'Text',
    videoLabel: 'Video',
    quizLabel: 'Quiz',

    trueFalseLabel: 'T/F',
    codeLabel: 'Code',
    codeEditorLabel: 'Code editor',
    blockCodeLabel: 'Block code',
    dragAndDropOrdenation: 'Drag and Drop ordenation',
    dragAndDropPositional: 'Drag and Drop positional',
    deleteSuccess: 'Activity deleted successfully',
    deleteFail: 'Ocorreu um erro ao deletar a atividade',
    addSuccess: 'There was an error deleting the activity',
    addError: 'Error adding class',
    modalConfirm: 'Are you sure you want to delete this activity?',
    modalWarning: 'This action cannot be undone.',
    manageActivity: {
      editActivity: 'Edit activity',
      errorTextTitle: "Can't find activity",
      generateQuestion: 'Generate Question',

      errorTextSubTitle: 'Verify if you are in correct page or contact support',
      newQuestion: 'New Question',
      save: 'Save',
      add: 'Add',
      detailsLabel: 'Info',
      notAllowed: 'Not allowed in limited edition',
      activityTypes: {
        TEXT: 'Text',
        VIDEO: 'Video',
        QUIZ: 'Quiz',
        TRUE_OR_FALSE: 'T/F',
        DND_ORDENATION: 'Drag',
        DND_POSITIONAL: 'Position',
        CODE: 'Code',
        CODE_EDITOR: 'Code editor',
        BLOCK: 'Block code',
      },
      examLabel: 'Exam',
      pdfLabel: 'PDF',
      taskLabel: 'Task',
      validationError: 'Error validating activities',
      saveSuccess: 'Activity saved successfully',
      deleteSuccess: 'Activity deleted successfully',
      saveError: 'An error occurred while saving the activity',
      activityDetails: {
        name: 'Activity Name:',
        inBank: 'Add in the Activity Bank',
        isPublicInBank: 'Public',
        config: '',
        noQuestionsWarning:
          'No questions created, click + New Question to add a question',
        save: 'Save',
        delete: 'Delete Activity',
        deleteError: 'There was an error deleting the activity',
        deleteSuccess: 'Activity deleted successfully',
        saveError: 'An error occurred while saving the activity',
        saveSuccess: 'Activity saved successfully',
      },
      activityBlock: {
        title: {
          label: 'Title',
          required: 'Title is required',
        },
        instructions: {
          label: 'Instructions',
          placeholder: 'Enter the instructions for the activity',
          required: 'Instructions are required',
        },
        tip: {
          label: 'Tip',
          placeholder: 'Optional: Add a tip if the student gets lost',
        },
        maxBlocks: 'Maximum number of blocks',
        maxBlocksAlert:
          'The maximum number of blocks cannot be less than the number of blocks already added',
        solution: {
          label: 'Solution code',
          required: 'Solution is required',
          validateFail: 'The solution code does not meet the expected output',
        },
        initial: {
          label: 'Initial code',
          required: 'Initial is required',
        },
        expectedOutput: {
          label: 'Expected Output',
          required: 'Expected Output is required',
        },
        validate: 'Validate',
        validateSuccess: 'Valid',
        validateFail: 'Invalid',
      },
      activityCode: {
        title: { label: 'Title', required: 'Title is required' },
        tip: {
          label: 'Tip',
          placeholder: 'Optional: Add a tip if the student gets lost',
        },
        instructions: {
          label: 'Instructions',
          placeholder: 'Insert the activity instructions',
          required: 'Instructions are required',
        },
        map: {
          run: 'RUN',
          reset: 'RESET',
          edit: 'Edit Map',
        },
        workspaceOptions: {
          title: 'Options',
          maxBlocks: 'Block Limit',
          maxBlocksAlert:
            'The block limit cannot be less than the number of blocks already added',
          coins: 'Coins',
        },
        save: 'Save',
        success: 'Activity saved successfully',
      },
      activityText: {
        title: 'Title:',
        titleError: 'Enter text title',
        subtitle: 'Subtitle',
        subtitleError: 'Enter text subtitle',
        content: 'Content:',
        contentError: 'Write something as the content of the question',
        save: 'Save',
        saveSuccess: 'Activity saved successfully',
        saveError: 'There was an error saving the activity',
      },
      activityVideo: {
        title: 'Title:',
        titleError: 'Enter video title',
        subtitle: 'Subtitle:',
        subtitleError: ' Enter video subtitle:',
        videoID: 'Video ID:',
        videoIDError: 'The video must have an ID',
        videoPlayer: 'Video Player:',
        videoPlayerError: 'Select one of the video players above',
        exampleMessage1: 'Example: Input ',
        exampleMessage2: ' from the URL ',
        save: 'Save',
        saveSuccess: 'Activity saved successfully',
        saveError: 'There was an error saving the activity',
      },
      activityQuiz: {
        answers: {
          answers: 'Answers:',
          noValue: 'Answer without content',
          cantRemove: 'The quiz must have at least 2 alternatives',
        },
        image: 'image',
        text: 'text',
        cantChangeType: 'Can not change quiz type. Create another quiz.',
        imageDescription: 'Image description: ',
        missingAlt: 'Add description of image',
        missingImages: 'Some alternatives are without image',
        title: 'Question:',
        content: 'Content:',
        titleError: 'Enter the activity question',
        answersError: 'At least two answers must be added',
        atLeastOneTrueAnswerError: 'One answer must be marked as correct',
        newAnswer: 'New Answer',
        justificative: 'Explanation of the correct answer:',
        justificativeError: 'The question needs a justification',
        save: 'Save',
        saveSuccess: 'Activity saved successfully',
        saveError: 'There was an error saving the activity',
      },
      activityTrueFalse: {
        title: 'Question:',
        content: 'Content:',
        titleError: 'Enter the activity question',
        trueOrFalse: 'The statement is true or false?',
        trueOrFalseError: 'An option must be selected as an answer',
        true: 'True',
        false: 'False',
        justificative: 'Explanation of the correct answer:',
        justificativeError: 'The question needs a justification',
        save: 'Save',
        saveSuccess: 'Activity saved successfully',
        saveError: 'There was an error saving the activity',
      },
      challenge: {
        title: 'Challenge',
        save: 'Save',
        edit: 'Edit Challenge',
        add: 'Add Challenge',
        deleteSuccess: 'Challenge deleted successfully',
        deleteFail:
          'There was a problem deleting the challenge, try again later or contact the support team',
        delete: 'Delete Challenge',
        cancel: 'Cancel',
        saveSuccess: 'Challenge successfully saved',
        saveError: 'There was an error saving the challenge',
        content: 'Content',
      },
      activityDNDOrdenation: {
        title: 'Question:',
        subtitle: 'Subtitle:',
        columns: 'Columns:',
        columnName: 'Column name:',
        text: 'Text',
        descriptionImage: 'Image description',
        newItem: 'New item',
        newColumn: 'Add new column',
        removeColumn: 'Remove column',
        save: 'Save',
        textTooltipRemoveColumn:
          'Can only remove if it has more than one column',
        textTooltipRemoveItem:
          'You can only remove if you have more than three items',
        nameColumnRequired: 'Column name is required',
        noAltText: 'Add a description to the image',
        noImage: 'Add an image',
        noContent: 'Add some content to the item',
        noTitle: 'Enter the activity question',
        saveSuccess: 'Activity saved successfully',
        saveOrderSuccess: 'New order saved',
        saveDeleteItemSuccess: 'Item deleted',
        saveAddItemSuccess: 'New item added',
        saveDeleteColumnSuccess: 'Deleted column',
        saveAddColumnSuccess: 'Added column',
        maxLength: 'The content must not exceed 150 characters',
        maxLengthAltText: 'Image description should not exceed 150 characters',
      },

      dndPositionalElementEdit: {
        saveSuccess: 'Activity saved successfully',
        saveOrderSuccess: 'New order saved',
        saveDeleteSuccess: 'Item deleted',
        saveAddItemSuccess: 'New item added',
        dndErrors: {
          onlyOneItem: 'Activity must contain more items',
        },
        dndActivityInfo: {
          title: 'Question:',
          subtitle: 'Subtitle:',
          dndErrors: {
            noTitle: 'Enter the activity question',
          },
        },
        dndColumnEdit: {
          columnName: 'Column name:',
          descriptionImage: 'Image description',
          newItem: 'New item',
          question: 'Question:',
          answer: 'Answer:',
          textTooltipRemoveItem:
            'You can only remove if you have more than one item',
          textTooltipReorderItem:
            'You can only reorder if you have more than one item',
          dndErrors: {
            nameColumnRequired: 'Column name is required',
            noTitle: 'Enter the activity question',
            noContent: 'Add some content to the item',
            noAltText: 'Add a description to the image',
            maxLength: 'The content must not exceed 150 characters',
            maxLengthAltText:
              'Image description should not exceed 150 characters',
          },
        },
        save: 'Save',
      },
    },
    activityBank: 'Activity bank',
  },

  changeLanguage: 'Change Language',
  configuration: 'Configuration',
  back: 'back',
  leaderboard: {
    classXp: '{{xp}}exp in class',
    rank: {
      position_one: '{{count}}<sup>st</sup>',
      position_two: '{{count}}<sup>nd</sup>',
      position_few: '{{count}}<sup>rd</sup>',
      position_other: '{{count}}<sup>th</sup>',
    },
  },
  dashboard: {
    hello: 'Hello, ',
    teacherUnderConstruction: 'New teacher dashboard under construction',
    wellcome: {
      teacher: 'Welcome back teacher! Ready to continue your journey? 🚀',
      admin: 'Welcome back admin!',
      unitAdmin: 'Welcome back unit admin!!',
      parent: "Welcome to Ctrl+Play, ready to transform your child's future?",
    },
    yourCourse: 'COURSE',
    accessClasses: 'Classes',
    totalClasses: 'CLASSES',
    totalActivities: 'ACTIVITIES',
    welcome:
      'Welcome å Ctrl + Play. Take a look at how our courses work and register!',
    dashboardClass: {
      yourClass: 'YOUR CLASS',
      teacher: 'Teacher',
      colleagues: 'Collegues',
      noTeacherText: 'Teacher not selected',
      teacherKlassMembers: `Select a class to see the avatars and access students' profiles`,
    },
    error: 'Could not load progress',
    support:
      'Looks like something went wrong. Please speak to the support team.',
    studentsPerformance: {
      title: 'Student Performance',
      description: `Based on <bold>{{studentsCount}}</bold> students' progress`,
    },
  },
  coursesView: {
    myCourses: 'My Courses',
    myLessons: 'My Lessons',
    myCoursesInstructions:
      'Here you can access all lessons and activities available in your course',
    completedCourses: 'COMPLETED COURSES',
    nextSteps: 'Next Steps',
    continueText:
      "Continue to evolve and learn with Ctrl+Play's most advanced courses.",
    student: {
      courses: 'Courses',
      noCourseProgressesFound: 'Could not find the course progress',

      filters: {
        ordering: 'Order by',
        search: 'Search',
        async: 'Autodidata',
        all: 'All',
      },
      section: {
        [ProgressStatusEnum.IN_PROGRESS]: 'in progress',
        [ProgressStatusEnum.CONCLUDED]: 'completed',
      },
    },
    admin: {
      search: 'Search',
      notFoundKlass: 'No class found',
      filters: {
        orderByFilter: {
          '-rating': 'best rated',
          rating: 'worst rated',
          title: 'select',
          created_at: 'least recent',
          '-created_at': 'most recent',
          '-last_update': 'recently updated',
          '-class_count': 'more classes',
          class_count: 'less classes',
        },
        groupByFilter: {
          NONE: 'select',
          STATUS: 'status',
          LINE: 'line',
        },
        all: 'all',
        category: 'Category',
        courses: 'Course',
        groupBy: 'Group by',
        orderBy: 'Order by',
        search: 'Search',
        version: 'Version',
      },
      groupBy: {
        all: 'See all',
        back: 'Back',
      },
      tooltip: {
        info: 'Informations',
        view: 'View',
      },
      versions: 'Versions',
      lastEdit: 'Edited {{dateDistance}}',
      createAt: 'Created {{dateDistance}}',
      publishedAt: 'Published {{dateDistance}}',
      klassesCount_one: '{{count}} Class',
      klassesCount_other: '{{count}} Classes',
      continueCreating: 'Keep creating',
      continueEdit: 'Continue editing',
      continueVersion: 'Continue versioning',
      createEdit: 'Create edit',
      createVersion: 'Create versioning',
      discardCourse: 'Discard course',
      errorTextTitle: 'Course not found',
      errorTextSubTitle: 'Check if you are in the right page',
      unpublishedCourses: 'Unpublished Courses',
      betaDraftExplanation:
        'The "Beta" courses below are available for some teachers to look at, review or make suggestions. "Draft" courses are still being edited and are not shown to teachers until the edits are complete.',
      courses: 'Courses',
      backEditing: 'Continue Editing',
      finish: 'Finish',
      publish: 'Publish Course',
      f2f: 'In person',
      online: 'Online',
      newCourse: 'New Course',
      newLesson: 'New Lesson',
      viewCourse: 'View Course',
      newVersion: 'New Version',
      editCourse: 'Edit Course',
      enableEditing: 'Enable Editing',
      myLessons: 'My Lessons',
      addExtraLesson: {
        add: 'Add',
        cancel: 'Cancel',
        selectLesson: 'Select a lesson',
        selectLessonExplain:
          'The extra lesson will be added before the selected lesson. You cannot select lessons that have already passed.',
        title: 'Select a lesson',
        newLesson: 'Lesson Title',
        allLessonClassIsDone: 'All lessons in this class are completed.',
        addExtraLessonQuestion:
          'Do you want to add an extra lesson after the lesson?',
      },
    },
    progress: 'PROGRESS',
  },
  profile: {
    title: 'My Profile',
    level: {
      levelName: 'Level {{level}}',
      levelProgress: '{{currentXp}}/{{targetXp}} EXP',
    },
    skills: 'Skills',
    errors: {
      username: 'This username already exists',
      links: 'Invalid links',
    },
    sixLinks: 'Is allowed add until 6 links',
    addLinks: 'Add Link',
    saveProfile: 'Save Profile',
    editProfile: 'Edit Profile',
    saveSuccess: 'Changes saved successfully',
    saveError:
      'There was a problem, try again later or contact the support team',
    notFoundTitle: 'Ops, no user found',
    notFoundSubtitle: "It looks like the user you're looking for doesn't exist",
    cancel: 'Cancel',
    create: 'Create your Profile',
    checklist: {
      avatar: 'Add avatar',
      banner: 'Add banner',
      links: 'Add social links',
      bio: 'Add bio',
    },
    bioCard: {
      title: 'This is your profile',
      description:
        'Click on edit profile button to add your Bio, social links, banner, or change your profile picture.',
    },
    totalActivities: 'Total Activitiess',
    completedCourses: 'Completed Courses',
  },
  skillPage: {
    title: 'Skills',
    general: 'General',
    total: 'Total',
    areaCard: {
      lastMonth_one: 'Last month',
      lastMonth_other: 'Last {{count}} months',
      progress: 'Progress',
    },
    chartOf: 'Chart of {{name}}',
    generalChart: 'General Chart',
  },
  klassView: {
    controls: {
      extraLesson: 'Extra Lesson',
      lesson: 'Lesson',
    },
    fullScreen: {
      exit: 'Exit fullscreen',
      enter: 'Fullscreen',
    },
    feedback: {
      feedbackInformation: `The feedback will be use by the educational team to improve the courses, don't forget to also ask the students to leave their feedback on the lesson.`,
      comment: 'Comment (optional)',
      content: 'Content',
      save: 'Save',
      saveError:
        'Ops, there was an error. Try again later or contact the support team',
      saveSuccess: 'Feedback saved successfully!',
      alertWarning: 'Add a rating before saving',
      contentAndActivity: 'Lesson content and activities',
      teacherExplanation: `Teacher's explanation`,
    },
    lessonNotFound: 'Can not find this lesson',
    instruction: {
      errorTextTitle: 'Video not found',
      errorTextSubTitle: 'The tool video was not found',
      vizualized: 'Viewed',
      accessAndInstalation: 'Access and Installation',
    },
    iconType: {
      access: 'Tool Access',
      activity: 'Activities',
      attendance: 'Presence',
      teacherFeedback: 'Feedback',
      studentFeedback: 'What did you think of the lesson?',
      challenge: 'Challenge',
      studentBook: 'Book',
      teacherBook: `Teacher's book`,
      homework: 'Homework',
      resources: 'Resources',
    },
    homework: {
      success: 'Answer submitted',
      notDone: 'Not done',
      waitingFeedback: 'Awaiting feedback',
      pending: 'Pending',
      revised: 'Revised',
      submit: 'Submit',
      feedback: {
        feedback: 'Feedback',
        addComment: 'Add comment to student',
        editComment: 'Edit comment for student',
        cancel: 'Cancel',
        grade: 'Grade',
        save: 'Submit to student',
        submittion: 'Submittion',
        notDone: 'Not done',
        hasChangesTooltip: 'There are changes that need to be saved',
        saved: 'Correction saved successfully',
      },
      error: {
        internalError:
          'An error occurred while trying to load the lesson. Contact support',
      },
    },
    noHomework: "There's no homework in this lesson",
    challengeView: {
      title: 'Challenge',
      visualized: 'Viewed',
      errorTextSubTitle: 'Check if you are in the right page',
      errorTextTitle: 'Challenge not found',
    },
    initialView: 'Class progress',
    noContent: "This class doesn't have a content yet",
    material: 'Material',
    studentMaterial: 'Student Material',
    teacherMaterial: 'Teacher Material',
    activity: 'Activities',
    challenge: 'Challenge',
    noneMaterial: 'No material available at the moment',
    toolAccess: 'Tool access',
    goBack: 'Go back',
    errorMessage: {
      homeworkPhrase: 'Unable to check and bring homework',
      progressPhrase: 'Unable to load your progress',
    },
    resources: 'Resources',
  },
  manageRecessCalendars: {
    manageRecessCalendars: 'Manage Recess Calendars',
    recessCalendar: 'Recess Calendar',
    recess: 'Recess',
    calendar: 'Calendar',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    dates: 'Dates',
    successMessage: 'Changes saved successfully',
    errorMessage: 'There was an error saving the information',
  },
  calendarPage: {
    calendar: 'Calendar',
    recess: 'Recess',
    classCalendar: 'Class Calendar',
    seeMore: 'see more',
    nextClasses: 'Next Classes',
    pastClasses: 'Past Classes',
    studentCalendarPhrase: 'Here you can see the date of the next classes 🗓️',
    noTeacher: 'No teacher',
  },
  calendar: {
    classCalendar: 'CLASS CALENDAR',
    seeMore: 'see more',
    student: {
      seeLess: 'see less',
      calendar: 'Calendar',
      upcomingClasses: 'UPCOMING CLASSES',
    },
    admin: {
      calendar: 'Classes Calendar',
      selectDates: 'SELECT THE DATES',
      nextClasses: 'Next Classes',
      allUnits: 'All units',
      filters: {
        allLessons: 'all lessons',
        completedLessons: 'completed lessons',
        plannedLessons: 'planned lessons',
      },
      noContent: {
        allLessons: 'No classes for this date',
        completedLessons: 'No classes completed on this date',
        plannedLessons: 'No classes planned for this date',
      },
      statisticCard: {
        classes: 'CLASSES',
        frequency: 'ATTENDANCE',
        weekFrequency: 'ATTENDANCE IN LAST 7 DAYS',
        monthFrequency: 'ATTENDANCE IN LAST 30 DAYS',
      },
      subtitleCard: {
        classesCompleted: 'classes completed',
        studentFrequency: 'students frequency',
      },
      manageKlass: {
        klassLink: 'Access class link',
        noKlassLink: 'This class has no link',
        report: 'Report',
        teacher: 'Teacher',
        delayedReport: 'Report Pending',
        noKlassTooltip: 'Try accessing it through the klass table',
        edit: 'edit',
        finish: 'finish',
        alter: 'alter',
        alterLessonDate: 'alter only this lesson',
        attendance: 'Attendance:',
        progress: 'Progress:',
        accessKlass: 'access class',
        alterLink: 'alter link',
        anticipate: 'anticipate lessons',
        postpone: 'postpone lessons',
        noStudentsInClass: 'No students, only admin can edit',
        manageAttendance: {
          attendances: 'Attendances:',
          absences: 'Absences:',
          reschedulings: 'Reschedulings:',
        },
        updateKlassLink: {
          updateKlassLinkTitle:
            'Do you want to add the link to the online classes?',
          success: 'Link updated successfully',
          error: 'Error updating link',
          placeholder: 'Insert Google Meet link',
          confirm: 'Confirm',
          cancel: 'Cancel',
        },
        updateDateKlass: {
          insertValidTime: 'Insert a valid time',
          updateDateKlassTitle:
            'Are you sure you want to change the date of this class?',
          selectDate: 'Select the new date for the next class:',
          confirmDate: 'Do you want to confirm the change?',
          success: 'Date changed successfully',
          error: 'Error changing date',
          confirm: 'Confirm',
          cancel: 'Cancel',
          or: 'or',
          postponeAnticipateWarning:
            'To change the date of multiple classes, use',
          timezoneWarning: 'Dates are in {{timezone}} time zone',
          noAvailableDatetime: 'No dates available',
          updateOtherLessonDate:
            'To change the date/time of this class, change the date/time of the previous class or the next class',
        },
        updateTeacher: {
          updateTeacherTitle:
            'Are you sure you want to replace the teacher for this class?',
          success: 'Replacement done successfully',
          error: 'Error replacing teacher',
          placeholder: 'Select another teacher',
          confirm: 'Confirm',
          cancel: 'Cancel',
        },
        anticipatePostpone: {
          warningResults:
            'This will be the new date for the next lesson, and all following classes will have their dates updated:',
          success: 'Dates changed successfully',
          confirm: 'Confirm',
          cancel: 'Cancel',
          noDateMessage: 'There are no dates for this action',
          postpone: {
            title:
              'Are you sure you want to postpone this and the next lessons?',
            warningMessage: 'Do you want to confirm postponing?',
            errorMessage: 'Error when postponing the lesson',
          },
          anticipate: {
            title:
              'Are you sure you want to antecipate this and the next lessons?',
            warningMessage: 'Do you want to confirm antecipating?',
            errorMessage: 'Error when antecipating the lesson',
          },
        },
        doNotAnticipateTooltip: 'It is not possible to anticipate this class',
      },
    },
  },

  scheduledLessonReportModal: {
    lessonStatus: {
      done: 'Completed',
      pending: 'Pending',
    },
    attendanceList: 'Attendance list',
    classItems: 'Class item',
    homework: 'Class homework',
    createHomework: 'Add homework',
    homeworkTitle: 'Homework',
    homeworkPlaceholder: 'Add a homework here to send to this class students',
    emptyContent: 'Enter a content for homework',
    homeworkCreated: 'Homework created successfully',
    homeworkUpdated: 'Homework updated successfully',
    homeworkDeleted: 'Homework removed successfully',
    homeworkDisabled: 'This lesson is set to not send homework',
    notes: 'Lesson notes',
    notesPlaceholder: 'General notes',
    notesUpdated: 'Lesson notes updated successfully',
    columns: {
      student: 'Student',
      description: 'Description',
      concluded: 'Concluded?',
      ocurrence: 'Occurrence',
      presence: 'Presence',
    },
    closeReport: 'Close report',
    submitLessonReport: 'Mark lesson as completed',
    presenceFilled: 'Attendance successfully recorded',
    planItemUpdated: 'Item successfully updated',
    noItems: 'No items found',
    copyScheduledLessonReport: 'Copy report',
    copyMessage: {
      klass: '📌 Class:',
      time: 'Time:',
      date: '📅 Date:',
      lessonUrl: '🎬 Lesson link:',
      teacher: '📙 Teacher:',
      objectives: '🎯 Lesson objectives:',
      homework: '🏆 Homework:',
      footer: '❓Any questions, please contact your school secretary.',
    },
  },

  manageScheduledLessonReport: {
    presentStudent: 'Student present',
    notifyLateless: 'Notify lateness',
    studentEntered: 'The student has already entered?',
    missingRequiredFields:
      'It is necessary to fill in the given content and the class summary to mark the class as completed',
    commentAlert:
      'The notification was closed because the teacher marked that the student entered the class at {{hours}} hours',
    notifySucess: 'The administrators have been notified about the delay',
    latelessUpdated: 'Delay updated',
    noReports: 'No reports found!',
    reportFromAnotherTeacherMessage:
      'This class is assigned to another teacher, it is not possible to complete this report.',
    contactAdminMessage:
      'If you taught this class, ask the unit administrator to change the teacher for this class.',
    hasNoPermission: 'You do not have permission to make this change',
    cantEditReport: 'You can not edit this report',
    publisInstructions:
      'This lesson is not published yet. Access edit page and finish all changes.',
    goEditPage: 'Access edit page',
    emptyContent: 'Enter a content for homework',
    cashMessage:
      'Students who did not miss class will receive Ctrl+Cash when the class was marked complete.',
    title: 'Class Completion Report',
    day: 'Day',
    notDone: 'Not done',
    students: 'Students',
    presence: 'Presence',
    hasDoneDesmark: 'Only an administrator can unmark this class as completed',
    challenge: 'Challenge',
    grades: 'Grades',
    placeholderLessonComment: 'Make remarks about {{student}} in this lesson',
    allValues: 'All values',
    hasSubmittion: 'Has submittion',
    noStudents: 'Without student',
    activeStudent: 'Active',
    isLockedDesc: 'Only an administrator can perform this action.',
    homeworkNotPublishedDesc:
      'Add a homerwork and your students will receive it',
    homeworkPublishedDesc: 'Homework published, waiting for student submittion',
    annotationsDesc: 'Class summary - What we learned today',
    hasHomeworkActivity: 'Do not send homework',
    seeHomework: 'See student Submittion',
    submittion: 'Submittion',
    noSubmittion: 'Student did not answer activity.',
    addGrade: 'Add Grade',
    missedPresenceStatus: 'M',
    nonMandatoryPhrase: 'It is not mandatory to fill in all fields.',
    notHomeworkPhrase: 'You have not yet added an assignment for this class.',
    notCtrlCashPhrase:
      'This lesson has already been marked as completed and you cannot change the Ctrl+Cash',
    pending: 'Pending',
    published: 'Published',
    draft: 'Draft',
    publish: 'Publish',
    homework: 'Homework',
    behavior: 'Behavior',
    addComments: 'Add Comments',
    practicalChallenge: 'Pratical Challenge',
    effort: 'Effort',
    cancel: 'Cancel',
    save: 'Save',
    reset: 'Reset',
    savedActiveStudent: 'Student activated',
    hasDone: 'Completed',
    gotoLesson: 'Go to lesson',
    selectStudentPhrase:
      'Select a student on the side by clicking on their name.',
    contentGiven: 'Content Given',
    contentGiveOptions: {
      [ContentGivenEnum.GAVE_100_PERCENT_AND_MORE]:
        'I managed to give all the content and more',
      [ContentGivenEnum.GAVE_100_PERCENT]: 'I gave 100% of the content',
      [ContentGivenEnum.GAVE_UP_T0_50_PERCENT]:
        'I gave up to 50% of the content',
      [ContentGivenEnum.GAVE_BETWEEN_50_99_PERCENT]:
        'I gave between 50-99% of the content',
    },
    selectAnOptionPhrase: 'Select an option',
    classNotes: 'Class notes:',
    canWritePhrase:
      'You can write down where you left off in class, or any other details.',
    homeworkDisplayedPhrase: 'Homework will be displayed to students',
    addHomeworkPhrase: 'Add a homework assignment for students.',
    classCompleted: 'Mark class as completed',
    classNotCompleted: 'Mark class as not completed',
    studentHasNoComments: 'This student has no comments',
    saved: 'Saved',
    savedScheduledLesson: 'Lesson alterations saved',
    savedHomework: 'Homework saved',
    savedReport: 'Report saved',
    savedGivenContent: 'Given content saved',
    selectAValidOptionPhrase: 'Select a valid option',
    requestMessages: {
      studentPresenceConfimed: 'Student attendance saved',
      studentPresenceRemoved: 'Student attendance removed',
      studentAbsenceConfirmed: 'Confirmed student absence',
      genericError: 'An internal error has occurred. Please contact support',
    },
    absences: 'Absences',
    presences: 'Presences',
    classCompletedButtonPhrase: 'Class completed',
    notPossibleToChangePhrase:
      'It is not possible to change the class after it is finished',
    annotationsPlaceholder:
      'You can write down where you left off in class, or any other details.',
    homeworkGradeMarker: {
      homeworkPreviousButton: 'Homework from the previous class',
      saved: 'Saved',
    },
    active: 'Active',
    inactive: 'Inactive',
    transfered: 'Transferred',
    noActiveStudentPhrase: 'No active students on the day of this lesson.',
    noReportForActiveStudent:
      '{{student}} does not have a report in this lesson',
    noActiveStudentsInKlass: 'There are no active students in this class',
    anotherKlassTag: 'Another class',
    selectStudentReport:
      'Select a student from the list to access their report in this area',
  },
  teacherDashboard: {
    hello: 'Hello,',
    visitor: 'Visitor',
    activeKlasses: {
      title: 'Active classes',
      seeAll: 'See all classes',
      today: 'Today',
      empty: 'No active classes with lessons this week',
      legend: {
        attendances: 'Attendances',
        exams: 'Exams',
        requiresAttention: 'Requires attention',
      },
    },
    yourLessons: {
      today: 'Today',
      nextLessons: 'Next lessons',
      empty: 'You have no scheduled lessons',
    },
    absenceReports: {
      newer: 'newest',
      older: 'oldest',
      markAsRead: 'Mark as read',
      openReport: 'Open Report',
      absenceReports: 'Absences Reports',
      markAllAsRead: 'Mark all as read',
      noAbsenceReports: 'No absences',
      allReadSuccessfuly: 'Notifications read successfully',
      klassNotFound: 'Class not found',
      groups: {
        klass: 'class',
        teacher: 'teacher',
        all: 'all',
      },
      totalItems_one: '{{count}}} item',
      totalItems_other: '{{count}} items',
      lastLessonsHelpMessage: {
        default: 'Present at {{lessonName}} lesson',
        MISSED: 'Missed {{lessonName}} lesson',
      },
      seeMore: 'see more',
    },
    teacherKlasses: {
      yourClasses: 'Your classes',
      informations: 'Informations',
      progress: 'Progress',
      classes: 'Classes',
      noStudents: 'No students',
      noClassesPhrase: 'There are currently no classes',
      noStudentsToFollow: 'No students to follow',
      sheet: 'Class sheet',
      studentSheet: 'Student sheet',
    },
    teacherReports: {
      pendingReports: 'Pending Reports',
      noReportsPhrase: 'There are no pending reports',
    },
    pendingHomeworks: {
      homeworkForCorrection: 'Homeworks for Correction',
      noHomeworks: 'No homework to correct',
      klassNotFound: 'Class not found',
      unassignedLessonLink:
        'Homeworks unavailable: no lesson assigned to this class',
    },
  },
  adminDashboard: {
    totalStudents: 'Total Students',
    totalKlasses: 'Total Classes',
    totalTeachers: 'Total Teachers',
    churnCount: 'Churn',
    attendanceCount: 'Attendance',
    studentsPerKlass: 'Students per Class',
    studentsPerTeacher: 'Students per Teacher',
    lastUpdated: 'Last updated',
    allUnits: 'All units',
    pendingReports: 'Pending Reports',
  },
  componentPagination: {
    next: 'Next',
    prev: 'Previous',
    totalOf: {
      students: 'Total of students:',
      klasses: 'Total of classes:',
      teachers: 'Total of teachers:',
      courses: 'Total of courses:',
      units: 'Total of units:',
      team: 'Total of members:',
      hackathons: 'Total of hackathons:',
    },
    of: 'of',
    pages: 'pages',
    page: 'page',
  },

  editMaterialPage: {
    editMaterial: 'Edit Material',
    legalInformation: 'Legal Information',
    save: 'Save',
    chapterTitle: 'Chapter Title',
    content: 'Content',
    createSlidesMaterial: 'Slide Material',
    chapters: 'Chapters',
    placeholderEditor: 'H2: For sections within chapters, use "Heading 2"',
    newChapterButton: 'New Chapter',
    generateChapterButton: 'Generate Chapter',
    generateChapterContent: 'Generate Content',
    saveButton: 'Save',
    cancelButton: 'Cancel',
    cantModify: 'Not allowed in limited editing',
    createChapterMessage: 'Added chapter',
    saveChapterMessage: 'Chapter successfully saved',
    saveOrderMessage: 'New order successfully saved',
    deletedChapterMessage: 'Successfully deleted chapter',
    oneChapterMessage: 'The book must contain at least one chapter',
    uploadMaterial: 'Upload Material',
    createMaterial: 'Create Material',
    deleteMaterial: 'Delete Material',
    bookIsBeingFormatedMessage: 'Please wait while the book is being formatted',
    deleted: 'Material deleted successfully',
    updateSuccess: 'Changes saved successfully',
    autosave: 'Autosave...',
    chapterFormattingFailureMessage:
      'An error occurred while formatting this chapter. Please check for any broken links, corrupted images, or empty spaces.',
  },

  rewardsView: {
    coinTooltip:
      'You can earn coins by completing activities, material, challenges, and more',
    gemTooltip:
      'You can earn gems by 100% completing a lesson or finishing some projects ',
    xpTooltip:
      'You can earn xp by doing activities, earning likes on projects, gaining attendance in classes, among others.',
    xp: '{{xp}} EXP',
    level: 'Level {{lvl}}',
    errors: {
      genericError: 'Unable to load rewards. contact support',
    },
  },

  modalRewards: {
    attentionPhrase: 'Are you sure you want to add or withdraw Ctrl+Cash?',
    rewardsButtons: {
      addCoins: 'add coins',
      withdrawCoins: 'withdraw coins',
    },
    inputPlaceholderPhrase: 'Enter what the student purchased',
    inputPlaceholderValue: 'Value',
    inputPlaceholderReason: 'Enter the reason for this transaction',
    confirmWithdrawButton: 'Withdraw',
    confirmDepositButton: 'Deposit',
    cancelButton: 'Cancel',
    select: {
      book: 'Book',
      activity: 'Activity',
      challenge: 'Challenge',
      homework: 'Homework',
      videoAccess: 'Video access',
      programAccess: 'Program access',
      lesson: 'Lesson',
      minorProject: 'Minor Project',
      majorProject: 'Major Project',
      behavior: 'Behavior',
      effort: 'Effort',
      presence: 'Presence',
      selectTheReason: 'Select reward reason',
    },
    messages: {
      transactionSuccess: 'Transaction done successfully',
      transactionWarning: 'Fill in all fields',
      genericError: 'An error occurred while fetching the rewards',
    },
  },

  modalHomework: {
    addHomeworkAssessment: 'Add homework assessment',
    notDone: 'Not done',
    confirmButton: 'Confirm',
    cancelButton: 'Cancel',
  },

  modalEmptyActivity: {
    attentionPhrase: 'Attention! This course has empty activity(s)',
    deletePhrase:
      'Do you want to delete the activity(s) before publishing it or continue editing?',
    confirmButton: 'Delete and Publish',
    cancelButton: 'Cancel',
    lesson: 'Lesson',
    extraLesson: 'Extra Lesson',
  },

  userForm: {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    dateOfBirth: 'Birth',
    username: 'Username',
    state: 'State',
    city: 'City',
    school: 'School',
    unit: 'Unit',
    phoneNumber: 'Phone Number',
    klass: 'Class',
    userType: 'Role',
    status: 'Status',
    cancel: 'Cancel',
    save: 'Save',
    saved: 'Saved',
    userStatus: userStatus,
    errors: {
      required: 'This field is required',
    },
  },
  projects: {
    page: {
      title: 'Projects',
      newProject: 'New project',
      addProjectTitle: 'Create project',
      updateProjectTitle: 'Edit project',
      ranking: 'Hackathon Ranking',
      noProjectsFound: 'No projects found',
      addProject: 'Add project',
      all: 'All',
      highlights: 'Most liked',
      viewed: 'Most viewed',
      hackathon: 'Hackathon',

      me: 'My projects',
      recents: 'Recent',
      filters: {
        allProjects: 'All',
        myProjects: 'My projects',
        searchPlaceholder: 'Search projects',
        mySchool: 'My school',
        myClass: 'My class',
        myCourse: 'My course',
        period: {
          allTime: 'All Time',
          lastMonth: 'Last Month',
          lastWeek: 'Last Week',
        },
      },
    },
    fields: {
      document: 'Document',
      imageOrVideo: 'Image/Video',
      units: { label: 'Unit', placeholder: 'Select unit' },
      publish: 'Create project',
      update: 'Save project',
      title: { label: 'Title', placeholder: 'Insert title' },
      description: { label: 'Description', placeholder: 'Insert description' },
      users: { label: 'Students', placeholder: 'Insert students' },
      importYourProject: 'Before anything, import your project',

      teacherMentor: {
        label: 'Teacher/Mentor',
        placeholder: 'Insert teacher/mentor',
      },
      projectUrl: {
        placeholder: 'Project link',
        label: 'Import',
        noMetadata:
          "We couldn't import the project's information, please add the title and images manually",
      },

      tool: { label: 'Tool', placeholder: 'Insert tool' },
      course: { label: 'Course', placeholder: 'Insert course' },
      equipName: {
        label: 'Team Name',
        placeholder: 'Insert team name',
        maxLength: 'The team name must have a maximum of {{max}} characters',
      },
      banner: {
        label: 'Banner',
        placeholder: 'Drag or click to add a banner',
        swapText: 'swap banner',
      },
      documentation: {
        label: 'Documentation',
        placeholder: 'Drag or click to add a file',
        filename: 'Project Documentation',
      },
      video: {
        label: 'Video',
        placeholder: 'Drag or click to add a video',
        filename: 'Project Video',
      },
      required: 'Required field',
      isHackathon_false: 'Is it a Hackathon?',
      isHackathon_true: "Is it a Hackathon? Don't forget to add:",
    },
    carousel: {
      seeAll: 'See all',
      back: 'Back',
    },
    error: {
      notHaveCourseFlag: 'The project needs to have a course flag',
    },
    card: {
      views: 'Views',
      likes: 'Likes',
      mentor: 'Mentor',
      position: {
        rank_ordinal_one: '{{count}}st place',
        rank_ordinal_two: '{{count}}nd place',
        rank_ordinal_few: '{{count}}rd place',
        rank_ordinal_other: '{{count}}th place',
      },
      unit: 'Unit',
      equip: 'Team',
      actions: {
        delete: 'Delete',
        edit: 'Edit',
        vote: 'Vote',
        rank: 'Rank',
        unvote: 'Unvote',
        none: 'none',
        finalist: 'Is it a finalist?',
      },
    },
    form: {
      cancel: 'Cancel',
      description: 'Description',
      projectUrl: 'Project link',
      publishProject: 'Publish project',
      selectTool: 'Select tool',
      title: 'Title',
      publishNewProject: 'Publish new project',
      projectUrlPlaceholder: 'Insert project link',
      titlePlaceholder: 'Insert project title',
      updateProject: 'Updat project',
      imageUploadFail: 'An error occurred while uploading image:',
      isStudentProject: 'Are you publishing a student project?',
      studentSelectPlaceholder: 'Select student',
      invalidLink: 'Invalid link',
      projectUrlRequired: 'Project link is required',
      titleRequired: 'Title is required ',
      toolRequired: 'Tool is required',
      imageRequired: 'Project image is required',
      successAddedMessage: 'Project successfully added',
      successUpdatedMessage: 'Project successfully updated',
    },
    view: {
      description: 'Description',
      classification: 'Classification',
      classificationPhrase: 'Has this project received any ratings?',
      projects: 'Projects',
      checkInfo: 'Check if you are in correct route',
      projectNotFound: 'Project not found',
      editProject: 'Edit',
      deleteProject: 'Delete',
      views_one: '{{count}} view',
      views_other: '{{count}} views',
      likes_one: '{{count}} like',
      likes_other: '{{count}} likes',
    },
  },

  inventory: {
    backpack: 'Backpack',
    save: 'Save',

    cancel: 'Cancel',
    alterFail: 'There was an issue saving the avatar',
    alterSuccess: 'Changes saved successfully',
    removeItem: 'Remove item',
    goToShop: 'Go to shop',
    purchaseSuccess: 'Purchase fulfilled successfully',
    purchaseError: 'There was an error trying to purchase the item',
    inventory: 'Inventory',
    shop: 'Shop',
    notEnoughCash: 'Not enough balance',
    confirmPurchase: "Are you sure you'd like to purchase the item below?",
    equipItem: 'Equip Item',
    confirm: 'Confirm',
    noItems: 'No item available',
    itemCategory: {
      base: 'Base',
      top: 'Top',
      bottom: 'Bottom',
      accessories: 'Accessories',
      equipment: 'Equipment',
    },
    inventoryItems: {
      hair: 'Hair',
      base: 'Body',
      hat: 'Hat',
      head: 'Head',
      face: 'Face',
      topFaceAcessory: 'Eyes',
      bottomFaceAcessory: 'Mouth',
      neckAcessory: 'Neck',
      shirt: 'Shirt',
      coat: 'Coat',
      pants: 'Pants',
      shoe: 'Shoe',
      hand: 'Hand',
      leftHandAcessory: 'Left Hand',
      rightHandAcessory: 'Right Hand',
    },
  },
  klassProgress: {
    myLessons: 'My Lessons',
    lessonProgressList: {
      didNotFindLesson: 'Could not find the lesson. Talk to support.',
      generalError:
        'An error occurred while trying to load the lesson progress',
      noLessons: 'It seems that there are no lessons. Talk to support.',
    },
  },

  nextLessonView: {
    noNextLesson: "Looks like you don't have an upcoming class scheduled.",
    noTeacher: 'Teacher not selected',
  },

  editActivity: {
    title: 'Question:',
    subtitle: 'Subtitle:',
    fields: {
      errors: {
        noTitle: 'Enter the activity question',
      },
    },
  },

  klassInput: {
    placeholder: 'Select a class',
    warnings: {
      noResults: 'No results',
      noStudentsToFollow: 'No students to follow',
    },
    errors: {
      internalError: 'An internal error has occurred. Contact support',
    },
  },

  rewardInput: {
    placeholder: 'Value',
  },

  lessonForm: {
    name: 'Class Title',
    description: 'Class Description',
    message: 'Motivational Message:',
    toolUrl: 'Program access link',
    instructionsUrl: 'Install/access the program',
    toolLinkMessage: 'Example: https://scratch.mit.edu/',
    videoInstallMessage: 'Example: https://www.youtube.com/watch?v=PWGX3QfAwCA',
    additHere: 'add it',
    saved: 'Changes saved successfully',
    tools: 'Tools',
    topics: 'Content',
    toolOption: 'Tool option',
    normal: 'Normal',
    free: 'Free',
    inBank: 'Add in the Lesson Bank',
    isPublicInBank: 'Public',
    links: 'Links',
    freeExplain:
      'When the content of the class is outside the curriculum, increasing the total number of classes in the course. Recommended for events or holidays.',
    normalExplain:
      'When you need to replace a tool or spend more time finishing a project. You can disable another class to keep the total of 18 classes.',
    addAuxiliaryLinks: 'Add links',
    warnings: {
      required: 'This field is required',
      validateLink: 'Enter a valid link',
      mainTool: 'Select a main tool',
    },
    toolPhrase: 'If the tool is not on the list, you can',
    addToolTitle: 'Add tool',
  },

  enrollmentStatus: {
    ACTIVE: 'Active',
    AWAITING_CLASS: 'Awaiting class',
    AWAITING_PAYMENT: 'Awaiting payment',
    AWAITING_PAYMENT_admin: 'Awaiting payment',
    AWAITING_PAYMENT_teacher: 'Enrollment processing',
    COURSE_TRANSFERRED: 'Course transfered',
    CLASS_TRANSFERRED: 'Class transfered',
    CONCLUDED: 'Graduated',
    CANCELED: 'Canceled',
    TRANSFERRED_FROM_CLASS: 'Class transferred',
    TRANSFERRED_TO_CLASS: 'Report in another class',
    CANCELED_IN_FUTURE: 'Canceled in future lessons',
    TRANSFERED_IN_FUTURE: 'Transfered in future lessons',
  },

  tagInput: {
    tool: 'Tools',
    mainToolBanner: 'Set as banner',
    editTag: 'Edit tag',
    banner: 'Banner',
  },

  tagForm: {
    name: 'Name',
    image: 'Image',
    link: 'Access link',
    color: 'Color',
    clear: 'Clear',
    confirm: 'Confirm',
    warnings: {
      required: 'This field is required',
      validateLink: 'Enter a valid link',
    },
    editSaved: 'Changes saved successfully',
    addSaved: 'Tag created successfully',
  },

  colorInput: {
    color: 'Color',
    clear: 'Clear',
    confirm: 'Confirm',
    warnings: {
      validateHexadecimalCode: 'Enter a valid color',
    },
  },

  listLessons: {
    successUpdateOrder: 'Updated class order',
    warnings: {
      noCourse:
        'The course could not be loaded. Please try again later or contact the support team',
    },
  },

  coursesTab: {
    warnings: {
      noCoursesPath: 'No courses found',
    },
  },

  klassStatus,

  activityCorrectionStatus,

  periods: {
    [KlassPeriodEnum.MORNING]: 'Morning',
    [KlassPeriodEnum.AFTERNOON]: 'Afternoon',
    [KlassPeriodEnum.EVENING]: 'Evening',
  },

  userTypes: {
    [UserTypeEnum.STUDENT]: 'Student',
    [UserTypeEnum.TEACHER]: 'Teacher',
    [UserTypeEnum.SUPER_ADMIN]: 'Super Admin',
    [UserTypeEnum.UNIT_ADMIN]: 'Unit Admin',
    [UserTypeEnum.PARENT]: 'Parent',
    [UserTypeEnum.CONTENT_CREATOR]: 'Content Creator',
  },

  courseType,

  userStatus: {
    REGISTRED: 'Registred',
    ACTIVE_ENROLLMENT: 'Active Enrollment',
    INACTIVE: 'Inactive',
    WAITING_ENROLLMENT: 'Waiting Enrollment',
  },
  ckeditor: {
    placeholder: 'Write here...',
    iframeBlocked: 'Iframe from "{{host}}" is not allowed and was removed.',
  },

  audioPlayer: {
    play: 'Play',
    pause: 'Pause',
    progress: 'Audio progress',
  },

  pedagogicalSheet: { ...sheet, title: 'Pedagogical Sheet' },

  scrollControl: {
    upButtonTooltip: 'Go to top',
    downButtonTooltip: 'Go to end',
  },

  commentAssessmentForm,

  occurrenceAssessmentForm,

  presenceStatus,

  studentAttentionRating: {
    1: 'Shows significant difficulty paying attention, with little response even after stimuli and redirections.',
    2: 'Frequently has difficulty maintaining focus, requiring constant interventions.',
    3: 'Maintains attention on short or more stimulating activities, but easily gets distracted in longer tasks.',
    4: 'Maintains attention in most activities, with few distractions.',
    5: 'Maintains attention consistently throughout the entire class, even in more complex activities.',
  },

  studentLearningRating: {
    1: 'Shows little assimilation of content, even with interventions, requiring individualized support.',
    2: 'Presents frequent difficulties and demands constant reinforcement and adaptations.',
    3: 'Assimilates content with the help of examples and specific reviews.',
    4: 'Assimilates content well, with partial autonomy and good performance.',
    5: 'Assimilates easily, applies content with autonomy and creativity.',
  },

  studentInterestRating: {
    1: 'Shows little interest in activities and rarely participates spontaneously.',
    2: 'Participates only when stimulated, with little initiative of their own.',
    3: 'Shows specific interest, participating with more involvement in specific topics.',
    4: 'Actively participates in most proposals, with good disposition.',
    5: 'Shows enthusiasm and frequent initiative, seeking to learn beyond what is proposed.',
  },

  studentSocializationRating: {
    1: 'Shows marked difficulty in interaction, may isolate themselves or generate conflicts.',
    2: 'Relates to few colleagues and needs frequent mediation.',
    3: 'Interacts functionally, but with some difficulties in group activities.',
    4: 'Relates well with colleagues, collaborating in most interactions.',
    5: 'Interacts with empathy, cooperates with the group and contributes positively to the class atmosphere.',
  },

  studentOrganizationRating: {
    1: 'Shows great difficulty in keeping materials and tasks organized, affecting learning.',
    2: 'Needs reminders and frequent monitoring to maintain minimal organization.',
    3: 'Presents basic organization, but with occasional lapses.',
    4: 'Keeps their materials and activities organized with partial autonomy.',
    5: 'Is highly organized, with responsibility and autonomy in managing their tasks.',
  },

  klassSheet: { ...sheet, title: 'Class Sheet' },

  assessmentHeader: sheet,

  behaviorAssessmentForm,

  courseFormats,

  businessModels,

  courseLineCategory,

  courseLineSubCategory,

  klassEnergyRating: {
    1: 'Quiet class',
    2: 'Class with good self-regulation',
    3: 'Class needing self-regulation strategies',
  },

  klassSpeedRating: {
    1: 'Slow class',
    2: 'Normal class',
    3: 'Fast class',
  },

  klassMaturityRating: {
    1: "Children's class",
    2: 'Normal class for age',
    3: 'Mature class',
  },

  buttonInfiniteList: {
    loadMore: 'Load more',
  },

  consentModal: {
    message:
      'We use cookies and local storage to enhance your browsing experience.',
    accept: 'Accept',
  },
  topics: {
    pageTitle: 'Topics',
    topbar: {
      new: 'Create new',
      searchPlaceholder: 'Search area',
      newArea: 'New area',
      save: 'Salvar',
      newTag: 'New tag',
    },
    tree: {
      createNew: 'Create a area',
    },
    workspace: {
      selectArea: 'Select a area',
    },
    moveTo: {
      cancel: 'Cancel',
      move: 'Move',
      placeholder: 'Search area',
      currentArea: 'current area',
      label: 'Move to',
      transformRoot: 'transform in root topic',
    },
    input: {
      label: 'Area',
      searchForTopics: 'Search for topics',
    },
    tooltip: {
      move: 'Move area',
      renaming: 'Rename',
      delete: 'Delete',
      area: {
        title: 'Area',
        desc: 'Areas can contain tags or other areas',
      },
      tag: {
        title: 'Tag',
        desc: 'Tags are content markers that add difficulty and level in an area',
      },
    },

    tags: {
      difficulty: {
        label: 'Difficulty',
        values: {
          ADVANCED: 'Advanced',
          INTERMEDIATE: 'Intermediate',
          BEGINNER: 'Beginner',
        },
      },
      level: {
        label: 'Nível',
        values: { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V' },
      },
      description: {
        label: 'Description',
        required: 'Description is required',
      },

      add: 'Add',
      cancel: 'Cancel',
      addDifficulty: 'Add difficulty',
      addContent: 'Add content',
    },
  },
  prompts: {
    pageTitle: 'AI Management',
    sidebarTitle: 'Prompts',
    workspace: {
      selectPrompt: 'Select a prompt',
    },
    form: {
      instructionLabel: 'Prompt',
      roleLabel: 'Role',
      filesLabel: 'Context files',
      filesInfo:
        'The files will be available for the AI to access and improve the knowledge base of both the parent prompt and the prompts belonging to it',
      roleInfo: 'The role is the context of the prompt',
      instructionInfo:
        'The instruction is the message that the model should respond',
      save: 'Save',
      success: 'Changes saved successfully',
      cancel: 'Cancel',
    },
    history: {
      title: 'Version history',
      latest: 'latest',
      version: 'v{{version}}',
      noHistory: 'No history available',
      loadingHistory: 'Loading history...',
    },
    comparator: {
      currentVersion: 'Current version',
      selectedVersion: 'Version {{version}}',
      restore: 'Restore',
      restoreSuccess: 'Version restored successfully',
      restoreError: 'Error restoring version',
    },
    categories: {
      QUIZ: {
        name: 'Quiz',
        description: 'Multiple choice question generation',
      },
      CODE: {
        name: 'Code',
        description: 'Code activity generation',
      },
      TRUE_OR_FALSE: {
        name: 'True or False',
        description: 'True or false question generation',
      },
      CODE_FIX: {
        name: 'Code Fix',
        description: 'Code correction and analysis',
      },
      COMPLETE_ACTIVITY: {
        name: 'Complete Activity',
        description: 'Complete activity generation',
      },
      LESSON_BLOCK: {
        name: 'Lesson Block',
        description: 'Lesson content block generation',
      },
      LESSON_MATRIX: {
        name: 'Detailed Matrix',
        description: 'Detailed lesson matrix generation',
      },
      OBJECTIVE: {
        name: 'Objectives',
        description: 'Used on lesson page for student interaction',
      },
      IMAGE_GENERATION: {
        name: 'Image Generator',
        description: 'Image generation for content',
      },
      COURSE_CREATION: {
        name: 'Course Creation',
        description: 'Course creation assistant',
      },
      SLIDE_MATERIAL: {
        name: 'Slide Material',
        description: 'Slide content generation',
      },
      ACTIVITY_CREATION: {
        name: 'Activity Creation',
        description: 'Activity creation assistant',
      },
      LESSON_CONTENT: {
        name: 'Lesson Content',
        description: 'Lesson chapter content generation',
      },
      STUDENT_SUPPORT: {
        name: 'Student Support',
        description: 'Assistants that support the student during the lesson',
      },
      CHATBOT: {
        name: 'Chatbot',
        description: 'Conversation assistant for the student in the lesson',
      },
    },
  },
  wellcome: {
    hello: 'Hello, {{firstName}} 👋',
    student: 'Welcome back, crewman! Ready to continue your journey? 🚀',
  },
  warning: {
    addAndEditClass:
      'Attention! It is not possible to add or edit classes through the Portal, only through the Store',
  },

  weekday,

  klassType: {
    REGULAR: 'Regular Class',
    CONECTA: 'Conecta Class',
    SCHOOL: 'School Class',
  },

  hideComponent: {
    options: {
      hiddenSection: 'Hidden section',
    },
  },

  hiddenComponents: {
    title: 'Hidden sections',
    activate: 'Activate',
  },

  modalHiddenComponents: {
    header: {
      title: "You've hidden the section from your dashboard!",
      paragraph:
        "Don't worry, if you want to see it again, just activate it from the list of hidden sections.",
    },
    footer: {
      paragraph: 'You can find this list at the bottom of your dashboard.',
      button: 'Got it!',
    },
  },

  hiddenComponent: {
    todayLessons: 'Today lessons',
    teachingWeek: 'Classes',
    yourLessons: 'Your lessons',
  },

  klassesFilters: {
    courseFormats: 'Format',
    courseLineId: 'Courses',
    dayOfWeek: 'Day',
    otherFilters: 'Other filters',
  },

  weekdayKlassCell: {
    termination: 'Termination',
  },

  nextLessonKlass: {
    warning: 'No active classes.',
  },

  klassPopup: {
    seeMore: 'See more',
    copyNames: 'Copy all names',
    copyEmails: 'Copy all e-mails',
  },

  useKlassOption: {
    info: 'Information',
    lessons: 'Lesson list',
    progress: 'Progress',
    sheet: 'Class sheet',
    members: 'Class members',
  },

  updates: {
    chatbot: {
      title: 'Now you can test our new AI assistant!',
      description:
        'Your course is available to test a chatbot with artificial intelligence to help you study the material, ask questions, among others! You can access the chatbot directly in your material by clicking the purple button.',
    },
    hackathon: {
      title: 'The hackathon has started!',
      description:
        'Start participating in the hackathon and have the possibility to win amazing prizes!',
      button: 'View hackathon',
    },
    terms: {
      title: 'Terms of Use',
      description: 'Please read our terms of use carefully before proceeding.',
    },
    notifications: {
      description:
        'Now you can receive administrative notifications about students and classes and quickly contact the guardians. For now, we have notifications for absences and tardies, with more improvements to be added soon!',
      title: 'Notifications',
    },
    notifyLatelessButton: {
      description:
        'You can now notify student delays to administrators to contact the guardians',
      title: 'Delay: Class Completion Report',
      secondDescriptionState:
        'If the student logs in, you can close the notification to prevent those responsible from being charged.',
      lastDescriptionState:
        'Comments that the secretariat makes are also available for you to read',
    },

    newDashProf: {
      description:
        'Now you can see the list of classes you have for today and also the reports that are pending in the same place',
      title: 'We made changes to simplify your dashboard!',
    },
    todayLessons: {
      description:
        'Now you can view which classes are scheduled for today. Your classes today can be filtered by past, ongoing and upcoming',
      title: "You can see today's classes!",
    },
    notificationsPageTeacher: {
      description:
        'To track student absences, you can open the "Notifications" tab and see absences, delays, homework, among others',
      title: 'Notifications tab',
    },
    vacationNotice: {
      title: 'Now you can create vacation notices for students!',
      description:
        'You can create a vacation notice in the last action of the student table',
      secondDescriptionState:
        'When an vacation notice is created, a tag is displayed on the classes that student will be absent from, on the class progress page and class report',
    },
    changeKlassStartDate: {
      title: 'Class Start Date',
      description:
        'Now administrators can change the class start date from here',
    },
    gotIt: 'Got it',
  },

  timeframeFilter: {
    ALL: 'All',
    ONGOING: 'Ongoing',
    PAST: 'Past',
    UPCOMING: 'Upcoming',
  },

  contacts: {
    noContacts: 'The user has no phones contact',
    student: 'Student',
    sendMessage: 'Send Message',
  },
  alerts: {
    title: 'Notifications',
    detail: {
      delay: {
        error: 'It was not possible to search for the notification details',
      },
    },
    page: {
      inbox: 'Inbox',
      filters: 'Filters',
      alert: 'You are in view mode. You do not have permission to make edits.',
    },
    list: {
      empty: 'No notifications',
      totalOf: 'Total notifications: {{count}}',
    },
    inputs: {
      commentPlaceholder: 'Type your comment',
    },
    buttons: {
      comment: {
        edit: 'Edit comment',
        add: 'Add comment',
      },
      fullScreen: 'Full screen',
      copyMessage: 'Copy Message',
      copyLink: 'Copy meet link',
      contact: 'Contact',
    },
    filters: {
      status: {
        all: 'all',
        today: 'today',
        yesterday: 'yesterday',
        chooseDate: 'Choose Date',
      },
      onlyPendings: 'Only Pendings',
      newest: 'Newest',
      oldest: 'Oldest',
      type: 'Type',
      unit: 'Unit',
      teacher: 'Teacher',
      klass: 'Class',
    },
    verbs: {
      [AlertTypeEnum.LATENESS]: 'has not entered the class yet',
      ABSENCE_one: 'missed a class',
      ABSENCE_other: 'missed {{count}} classes in a row!',
      [AlertTypeEnum.HOMEWORK_NOT_DONE]: "didn't do the homework",
      [AlertTypeEnum.OCCURRENCE]: 'received an occurrence',
    },
    types: {
      [AlertTypeEnum.LATENESS]: 'lateness',
      [AlertTypeEnum.ABSENCE]: 'absence',
      [AlertTypeEnum.HOMEWORK_NOT_DONE]: 'homework',
      [AlertTypeEnum.OCCURRENCE]: 'occurrence',
    },
    messages: {
      latelessBody:
        'We noticed that {{studentName}} has not entered the class {{lessonName}} today that started at {{lessonDate}}.',
      absenceBody:
        'We noticed that {{studentName}} missed the {{lessonName}} of day {{lessonDate}}.',
      homeworkNotDoneBody:
        "We noticed that {{studentName}} didn't do the homework for class {{lessonName}}.",
      occurrenceBody: 'We have received notification about the {{studentName}}',
      link: 'You can access the lesson through this link: {{link}}',
      simpleBody:
        "We noticed that {{studentName}} did not enter today's class.",
      presentation: 'Hello, how are you? This is {{sender}}.',
      footer: 'Thank you for your attention!',
      question: 'Can you inform us of the reason?',
    },
  },
  relationships: {
    FATHER: 'Pai',
    MOTHER: 'Mãe',
    BROTHER: 'Irmão',
    SISTER: 'Irmã',
    UNCLE: 'Tio',
    AUNT: 'Tia',
    GRANDFATHER: 'Avô',
    GRANDMOTHER: 'Avó',
    OTHER: 'Responsible',
  },

  filters: {
    ...courseFormats,
    ...courseType,
    ...weekday,
    ...klassStatus,
    startSoon: 'Start soon',
    endSoon: 'End soon',
    hasActiveLessons: 'Has active lessons',
  },

  vacationNotice: {
    title: 'Vacation notice',
  },

  klassGroupButton: {
    tooltip: 'The class does not have a link to the group',
    textButton: 'Class group',
  },
  editorJs: {
    ui: {
      blockTunes: {
        toggler: {
          'Click to tune': 'Click to tune',
          'or drag to move': 'or drag to move',
        },
      },
      inlineToolbar: {
        converter: {
          'Convert to': 'Convert to',
        },
      },
      toolbar: {
        toolbox: {
          Add: 'Add',
        },
      },
    },

    toolNames: {
      Text: 'Paragraph',
      Heading: 'Heading',
      List: 'List',
      Warning: 'Warning',
      Checklist: 'Checklist',
      Quote: 'Quote',
      Code: 'Code',
      Delimiter: 'Delimiter',
      'Raw HTML': 'Raw HTML',
      Table: 'Table',
      Link: 'Link',
      Marker: 'Marker',
      Bold: 'Bold',
      Italic: 'Italic',
      InlineCode: 'Inline Code',
      Código: 'Code',
    },

    tools: {
      warning: {
        Title: 'Title',
        Message: 'Message',
      },

      link: {
        'Add a link': 'Add a link',
      },

      stub: {
        'The block can not be displayed correctly.':
          'The block can not be displayed correctly.',
      },
    },

    blockTunes: {
      imageTune: {
        Crop: 'Crop',
        Apply: 'Apply',
        Resize: 'Resize',
      },
      delete: {
        Delete: 'Delete',
      },
      moveUp: {
        'Move up': 'Move up',
      },
      moveDown: {
        'Move down': 'Move down',
      },
    },
    placeholders: {
      header: 'Type a header',
      paragraph: 'Type a text',
      rawHtml: 'Enter HTML code',
    },
    label: {
      imageGenerator: 'Enter your prompt and generate the image',
    },
  },

  codeEditor: {
    run: 'Run',
    reset: 'Reset',
    notStarted: 'Compiler not started',
    validateCode: 'Validate code',
  },
  section: {
    notDeleteUniqueSection: 'The subchapter must have at least one section',
    title: 'New section',
    form: {
      save: 'saved',
      startsHidden: 'Start hidden',
      deleteSection: 'Delete section',
      startsVisible: 'Start visible',
      title: { required: 'Title is required' },
    },
    quizActivityForm: {
      createTitle: 'Create a quiz activity',
      editTitle: 'Edit a quiz activity',
      question: 'Question',
      content: 'Content',
      answers: 'Answers',
      newAnswer: 'New answer',
      explain: 'Explanation of the correct answer',
      name: 'Name',
      minAnswer: 'Should have at least 2 answers',
      onlyOneCorrectAnswer: 'Only one answer can be marked as correct',
      atLeastOneCorrectAnswer: 'Should have at least one correct answer',
    },
  },
  book: {
    form: {
      title: 'Slides material',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      cantDeleteBook: 'It was not possible to delete the book',
    },
  },
  chapter: {
    title: 'Chapter',
    notDeleteUniqueChapter: 'The book must have at least one chapter',
    list: {
      title: 'Content',
      add: 'Add',
    },
    layout: {
      layout1: 'Layout 1',
      layout2: 'Layout 2',
      layout3: 'Layout 3',
      custom: 'Custom',
      finish: 'Finish',
    },
    form: {
      finishLayout: 'Finish layout',
      editLayout: 'Edit layout',
      addSection: 'Add section',
      deleteSubchapter: 'Delete subchapter',

      title: {
        required: 'Title is required',
      },
    },
    successDeleting: 'Chapter deleted successfully',
    successUpdated: 'Chapter updated successfully',
  },
  subchapter: {
    title: 'Subchapter',
    notDeleteUniqueSubchapter: 'The chapter must have at least one subchapter',
  },
  hackathon: {
    votingSoon: 'Voting stage starts soon',
    votingClosed: 'Voting stage has ended',
    stages: {
      [StageTypeEnum.SUBMISSION]: 'Submissions',
      [StageTypeEnum.FINAL_VOTING]: 'Final Voting',
      [StageTypeEnum.INITIAL_VOTING]: 'Initial Voting',
    },
    form: {
      hackathon: {
        successCreatedMessage: 'Hackathon created successfully',
        successUpdatedMessage: 'Hackathon updated successfully',
        required: 'Campo obrigatório',
        edition: {
          label: 'Edition',
          placeholder: 'Enter the edition',
          numberValidate: 'This field must be a number',
        },
        theme: {
          label: 'Theme',
          placeholder: 'Enter the theme',
        },
        content: {
          label: 'Content',
          placeholder: 'Enter the content',
        },
        banner: {
          label: 'Banner',
        },
      },
      hackathonStages: {
        successCreatedMessage: 'Hackathon stage created successfully',
        successUpdatedMessage: 'Hackathon stage updated successfully',
        type: {
          label: 'Type',
          placeholder: 'Select type',
        },
      },
    },
    steps: 'Steps:',
    building: 'Under construction',
    shunfle: 'Shuffle',
    noHackathon: 'It seems there is no hackathon happening',

    countdown: {
      finishAt: 'finishes at:',
      startAt: 'starts at:',
    },
    banner: {
      tematic: 'Theme',
      winnersPhrase: 'The top 3 winners will receive amazing prizes!',
      liveStream: 'Access the live stream on the day of the final',
      liveStreamLink: 'clicking here',
      recordLiveStream: 'Watch the Hackathon {{edition}} final by',
    },
  },
  codeEditorLanguage: {
    JAVASCRIPT: 'JavaScript',
    PYTHON: 'Python',
  },

  codeEditorForm: {
    initialCode: 'Base code (optional)',
    expectedOutput: 'Expected result',
    addTestCase: 'Add',
    removeTestCase: 'Remove',
    input: 'Value(s) to be entered',
    testCase: 'Test {{count}}',
    testCases: 'Test cases',
    solutionCode: 'Solution code',
    validate: 'Validate',
    valid: 'Valid',
    invalid: 'Invalid',
    validating: 'Wait, validating the tests',
    invalidCode: 'The tests failed! Check the test cases and the solution code',
    testCasesInfo: {
      tooltip: 'Test cases information',
      inputExplain:
        "The data that will be provided to the student's code for processing.",
      singleInput: '• For one value: enter just the value (e.g., 5)',
      multipleInput:
        '• For multiple values: enter each value on a separate line',
      expectedOutputExplain:
        "The correct result that the code should produce when it receives the input. Used to verify if the student's solution is working correctly.",
      howToReadInput: {
        title: 'How to read input in code ({{language}}):',
        explain:
          'For the test cases to work, the code must use {{inputMethod}} to read the data.',
        example: {
          singleInput: 'Example - One value:',
          multipleInput: 'Example - Multiple values:',
        },
      },
    },
  },

  resourcePreview: {
    changeResource: 'Change {{resource}}',
  },

  modalEditResource: {
    title: 'Change {{resource}} from resource',
  },
  resourceEditor: {
    isMandatory: 'Is mandatory?',
    onlyTeacher: 'Only teachers?',
  },

  resourceActivities: {
    activityAlreadyIncluded: 'Activity already included in the resource',
  },

  dayOfWeek: weekday,

  studentType,

  codeValidation: {
    passedValues: 'Passed values',
    expect: 'Expected',
    returned: 'Returned',
    test: 'Test',
    passed: 'Passed',
    failed: 'Failed',
    notStdin: 'Attention! Your code does not have any input',
    runTests: 'Run tests',
    beforeDeliveringActivity:
      'Before delivering the activity, you can check if your code meets the task requirements',
  },
  feedbackAuthor: {
    showAuthor: 'Show author',
    hideAuthor: 'Hide author',
  },

  updateNotification: {
    message: 'A new version is available. Do you want to update?',
    update: 'Update',
  },

  courseStudio: {
    name: 'Name',
    type: 'Type',
    format: 'Format',
    abbreviation: 'abbreviation',
    duration: 'Duration',
    courseLine: 'Course Line',
    courseModule: 'Module',
    lessonsQuantity: 'Number of lessons',
    required: 'This field {{field}} is required',
    allowFirstStudioStepMessage:
      'For now, this studio step is only allowed for the first version of the course',
    courseInformation: 'Course Informations',
    fillInTheCourseInformation: 'Fill in the course information',
    courseObjectives: 'Course Objectives',
    shouldBeAbleTo: 'At the end of the course, students should be able to:',
    addAnObjective: 'Add an objective',
    curriculumMatrix: 'Syllabus',
    block: 'Block {{index}}',
    lesson: 'Lesson',
    addLesson: 'Add lesson',
    addBlock: 'Add block',
    dragOrAddLesson: 'Drag or add a lesson to the block',
    addObjective: 'Add objective',
    revertActions: 'Revert actions',
    maxLessonsReached: 'You have reached the limit of lessons defined',
    maxBlocksReached: 'You have reached the limit of blocks defined',
    lessonsQuantityMismatch:
      'The number of lessons ({{actual}}) does not match the defined value ({{expected}})',
    directLessonsWithBlocksMismatch:
      'It is not possible to have lessons outside of a block, please put all lessons in some block',

    matrixDetails: 'Detailed Matrix',
    lessonDetails: 'Lesson Details',
    noDescription: 'No description provided',
    descriptionPlaceholder: 'Enter lesson description...',
    endLessonTheStudentShould:
      'At the end of the class the student will be able to',
    chapters: 'Chapters',
    addChapter: 'Add chapter',
    addSubChapter: 'Add subchapter',
    chapterPlaceholder: 'Chapter of the lesson',
    subchapterPlaceholder: 'Subchapter of the lesson',
    noChapters: 'No chapters',
    noSubChapters: 'No subchapters',
    notAllowedLimitedEditing: 'Not allowed in limited editing',
    notAllowedIfNotAtLeastInLimitedEdition:
      'Not allowed if not at least in limited edition',
    actions: {
      generateMatrix: {
        locked: 'Create and save the objectives first',
        name: 'Generate matrix',
      },
      generateObjectives: {
        locked: 'Create and save the course first',
        name: 'Generate objectives',
      },
      generateLessonDetail: {
        locked: 'Create and save the course first',
        name: 'Generate lesson detail',
      },
    },
  },

  courseDuration: {
    [CourseDurationMinuteEnum.ONE_HOUR]: '1h',
    [CourseDurationMinuteEnum.ONE_HOUR_AND_A_HALF]: '1h30',
    [CourseDurationMinuteEnum.TWO_HOURS]: '2h',
    [CourseDurationMinuteEnum.TWO_HOURS_AND_A_HALF]: '2h30',
    [CourseDurationMinuteEnum.THREE_HOURS]: '3h',
  },

  courseLessons: {
    title: 'My Lessons',
    header: {
      title: 'My <highlight>Lessons</highlight>',
    },
    unitCard: {
      lessons: 'Lessons',
      completedCount: '{{completed}} of {{total}} completed',
      lessonsCount_one: '{{count}} lesson',
      lessonsCount_other: '{{count}} lessons',
      progressLabel: 'Unit progress',
      shortcuts: {
        flashcards: 'Flashcards',
        materials: 'Materials',
        resources: 'Resources',
      },
    },
    unitList: {
      empty: 'This course has no units yet.',
      loadError: 'Could not load the units.',
    },
    lessonList: {
      empty: 'This unit has no lessons yet.',
      loadError: 'Could not load the lessons of this unit.',
    },
    lessonCard: {
      notStarted: 'Not started',
      progressLabel: 'Lesson progress',
      items: {
        instruction: 'Instr.',
        material: 'Material',
        activity: 'Activ.',
        teacherBook: 'Teach.',
        challenge: 'Challenge',
        resources: 'Resour.',
      },
    },
    staff: {
      course: 'Course',
      klass: 'Class',
      coursePlaceholder: 'Select a course',
      klassPlaceholder: 'All classes',
      selectCourseOrKlass: 'Select a course or class to see the lessons',
    },
    lessonPage: {
      title: 'Lesson',
      tabsLabel: 'Lesson content',
      pendingBadgeLabel: 'Pending: {{value}}',
      progressLabel: 'Lesson progress',
      notFound: {
        title: 'Lesson not found',
        subtitle: 'This lesson does not exist or you do not have access to it.',
      },
      tabs: {
        instruction: 'Instructions',
        activity: 'Activities',
        material: 'Material',
        teacherBook: "Teacher's Material",
        challenge: 'Challenges',
        resources: 'Resources',
      },
      navigation: {
        label: 'Lesson navigation',
        previous: 'Previous lesson',
        next: 'Next lesson',
      },
      instructions: {
        title: 'Instructions',
        loadError: 'Could not load the instructions for this lesson.',
      },
      resources: {
        title: 'Resources',
        loadError: 'Could not load the resources for this lesson.',
        download: 'Download {{title}}',
        view: 'View {{title}}',
      },
      material: {
        title: "Student's Material",
        teacherTitle: "Teacher's Material",
        loadError: 'Could not load the material for this lesson.',
        empty: 'This lesson does not have published chapters yet.',
        status: {
          read: 'Read',
          pending: 'Pending',
        },
        unavailable: {
          title: 'Chapter unavailable',
          subtitle: 'This chapter does not exist or is no longer published.',
          back: 'Back to the material',
        },
        pdf: {
          unavailable: {
            title: 'Could not open the PDF',
            subtitle: 'Check your connection and try again.',
          },
        },
        navigation: {
          label: 'Chapter navigation',
          previous: 'Previous',
          next: 'Next',
        },
        selector: {
          title: 'Chapters',
          description: 'Select below the chapter you want to access',
          change: 'Change chapter',
        },
      },
      challenges: {
        title: 'Challenges',
        loadError: 'Could not load the challenge for this lesson.',
        markAsViewed: 'Mark as viewed',
        viewed: 'Viewed',
        viewedTooltip: 'You have already marked this challenge as viewed',
        unavailableTooltip:
          'This challenge cannot be marked as viewed right now',
      },
      activities: {
        title: 'Activities',
        loadError: 'Could not load the activities for this lesson.',
        empty: 'This lesson has no activities yet.',
        questionCount_one: '{{count}} question',
        questionCount_other: '{{count}} questions',
        status: {
          done: 'Completed',
          pending: 'Pending',
        },
      },
    },
  },

  studentDashboard: {
    header: {
      hello: 'Hello',
    },
    courseSelect: {
      changeCourse: 'Change course',
      close: 'Close',
      myCourses: 'My courses',
      explore: 'Explore',
      selectCourse: 'Select a course',
    },
    progress: 'Progress',
    stats: {
      lessons: 'Lessons',
      activities: 'Tasks',
      challenges: 'Challenges',
    },
    description: 'of {{total}} {{type}}',
    assisted: 'Assisted',
    concluded: 'Concluded',
    completed: 'Completed',
    shortcuts: {
      title: 'Quick shortcuts',
      loadError: 'Could not load the shortcuts',
      lessons: 'Lessons',
      schedule: 'Schedule',
      exams: 'Exams',
      projects: 'Projects',
      performance: 'Performance',
    },
    courseTrack: {
      title: 'Course track',
      bannerAlt: 'Banner of {{lesson}}',
      access: 'Access',
      seeAll: 'See all',
      seeAllUnits: 'See all units',
      empty: 'No lessons available in the track',
      loadError: 'Could not load the course track',
    },
    continuity: {
      bannerAlt: 'Banner of {{lesson}}',
      completedPercentage: '{{percentage}}% completed',
      continueLesson: 'Continue',
      loadError: 'Could not load the next lesson',
      pendencies: {
        scheduledExam: 'Scheduled exam',
        nextLesson: 'Next lesson',
        pendingHomeworks: 'Homework',
        doUntil: 'Complete by: {{date}}',
        happensOn: 'Happens on {{date}}',
        loadError: 'Could not load the pendencies',
      },
    },
  },

  lessonProgressCollection: {
    title: 'Your lessons',
  },

  courseBox: {
    actions: {
      view: 'View',
      putInBeta: 'Put in beta',
      discard: 'Discard',
      legalInformation: 'Legal information',
      publish: 'Publish',
    },
    form: {
      format: 'Format',
      duration: 'Duration',
      abbreviation: 'Abbreviation',
      objective: 'Objective',
      tool: 'Tool',
      units: {
        label: 'Units',
        placeholder: 'Select the unit',
      },
      inBank: 'Add in the Activity Bank',
      isPublicInBank: 'Public',
    },
  },
  nextLessonDisplay: {
    title: 'Next lesson',
    countdown: {
      days: 'days',
      hrs: 'hrs',
      min: 'min',
      sec: 'sec',
    },
    classroom: 'Classroom',
    at: 'at',
  },
  extraCoursesPortal: {
    extraCourses: 'Extra Courses',
    myCourses: 'My Courses',
    inProgress: 'In Progress',
    completed: 'Completed',
    progress: 'Progress',
    continue: 'Continue',
    review: 'Review',
    statusInProgress: 'In progress',
    statusCompleted: 'Completed',
    noExtraCourses: "You don't have any extra courses available yet",
    errorLoadingCourses: 'Error loading extra courses. Please try again.',
  },

  courseCard: {
    progressStats: {
      totalLessons_one: '{{count}} Lesson',
      totalLessons_other: '{{count}} Lessons',
      remainingLessons: 'Remaining {{remaining}} lessons',
      lessonProgress: 'Lesson progresses',
    },
  },

  questionBank: {
    title: 'Question Bank',
    open: 'Import from question bank',
    columns: {
      name: 'Block name',
      questions: 'Questions',
      usedIn: 'Used in',
    },
    questionsCount_one: '{{count}} question',
    questionsCount_other: '{{count}} questions',
    usedInLessons_one: '{{count}} Lesson',
    usedInLessons_other: '{{count}} Lessons',
    notUsed: 'Not used',
    usedInInfo: 'See where the block is used',
    empty: 'No blocks found',
    filters: {
      searchPlaceholder: 'Search by name',
      subCategory: 'Subcategory',
      educationalParameter: 'Educational parameter',
    },
    scopes: {
      ACTIVITY: 'Question Blocks',
    },
    panel: {
      types: 'Types',
      filters: 'Filters',
      location: 'Location',
      course: 'Course',
      lessonBlock: 'Unit',
      lesson: 'Lesson',
      all: 'All',
      typology: 'Typology',
      author: 'Author',
      tool: 'Tool',
      authors: {
        ALL: 'All',
        MINE: 'Mine',
        PEDAGOGICAL: 'Pedagogical',
        TEACHER: 'Teacher',
      },
    },
  },
  courseLineForm: {
    fileTree: {
      courses: 'Courses',
      newCourse: 'New Course',
    },
    layout: {
      back: 'back',
    },
    form: {
      courseName: 'Course Name',
      courseNamePlaceholder: 'Enter the course name',
      courseNameRequired: 'Course name is required',
      courseCover: 'Course Cover',
      courseCoverAlt: 'Course cover',
      category: 'Category',
      categoryRequired: 'Category is required',
      subcategory: 'Subcategory',
      subcategoryPlaceholder: 'Select the subcategory',
      subcategoryRequired: 'Subcategory is required',
      save: 'Save',
    },
    workspace: {
      course: 'Course',
      newCourse: 'New Course',
      tabs: {
        information: 'Information',
        modules: 'Modules',
        evaluations: 'Evaluations',
      },
    },
    evaluationsTab: {
      title: 'Evaluations',
      infoAlertTitle: 'Configure the evaluations',
      infoAlertDescription:
        'The evaluations created here are tied to the pedagogical content of this course. You can change the evaluation information later.',
      addBlock: 'Add Evaluation Block',
      addAssessment: 'Add evaluation',
      blockName: 'Evaluation Block Name',
      blockNamePlaceholder: 'Enter the block name',
      blockNameRequired: 'Block name is required',
      cancel: 'Cancel',
      save: 'Save',
      createBlockSuccess: 'Evaluation block created successfully',
      deleteBlockSuccess: 'Evaluation block deleted successfully',
      updateBlockSuccess: 'Evaluation block updated successfully',
      reorderAssessmentsSuccess: 'Assessments order updated successfully',
      assessmentName: 'Evaluation name',
      assessmentNamePlaceholder: 'Enter the evaluation name',
      assessmentNameRequired: 'Evaluation name is required',
      assessmentDescription: 'Evaluation description (optional)',
      assessmentDescriptionPlaceholder: 'Enter the description',
      assessmentMaxGrade: 'Weight of this evaluation',
      assessmentMaxGradePlaceholder: '0 to 100',
      assessmentMaxGradeMax: 'Max: 100%',
      assessmentMaxGradeRequired: 'Weight is required',
      assessmentMaxGradePercentage:
        'The value is a percentage (between 0 and 100)',
      pedagogicalContentSection: 'Pedagogical content',
      selectUnitsPlaceholder: 'Select units',
      noUnitsAvailable: 'No units available',
      pedagogicalContentMissingTitle: 'Pedagogical content missing!',
      pedagogicalContentMissingDescription:
        'This course does not have pedagogical content yet. You can configure the evaluation, but it will remain a draft until the content is registered.',
      goToPedagogicalContent: 'Go to Ped. Content',
      assessmentItemSection: 'Evaluation item',
      addAssessmentItem: 'Add',
      addAssessmentItemDisabledTooltip:
        'Save the evaluation first to add items',
      newAssessmentItemFormTitle: 'New Evaluation Item',
      assessmentItemFormTitle: 'Edit Evaluation Item',
      assessmentItemName: 'Item name',
      assessmentItemNamePlaceholder: 'Enter the item name',
      assessmentItemNameRequired: 'Item name is required',
      assessmentItemDescription: 'Description (optional)',
      assessmentItemDescriptionPlaceholder: 'Enter the description',
      assessmentItemType: 'Type',
      assessmentItemTypeRequired: 'Type is required',
      assessmentItemTypeManual: 'Regular',
      assessmentItemTypeDigitalActivity: 'Digital Content',
      assessmentItemTypeTest: 'Test',
      assessmentItemTypeChangeBlockedByCriteria:
        'Test type cannot have criteria',
      assessmentItemMaxGrade: 'Max Grade',
      assessmentItemMaxGradePlaceholder: 'Enter a percentage',
      assessmentItemMaxGradeRequired: 'Max grade is required',
      assessmentItemMaxGradeRange: 'Max grade must be between 0 and 100',
      assessmentItemMaxGradeMaxBadge: 'Max: {{max}}',
      assessmentItemMaxGradeExceedsAssessment:
        'The sum of item grades exceeds the assessment max grade which must be {{assessmentMaxGrade}}',
      assessmentItemColumnName: 'Name',
      assessmentItemColumnType: 'Type',
      assessmentItemColumnMaxGrade: 'Max. score',
      assessmentItemTotal: 'Total',
      criteriaSection: 'Criteria',
      addCriteria: 'Add criteria',
      criteriaName: 'Criterion name',
      criteriaNamePlaceholder: 'Enter the criterion name',
      criteriaNameRequired: 'Criterion name is required',
      criteriaDescription: 'Criterion description (optional)',
      criteriaDescriptionPlaceholder: 'Enter the description',
      criteriaMaxGrade: 'Criterion max grade',
      criteriaMaxGradePlaceholder: 'Enter the max grade',
      criteriaMaxGradeRequired: 'Max grade is required',
      criteriaMaxGradeRange: 'Max grade must be between 0 and {{max}}',
      criteriaMaxGradeZero: 'Max grade must be 0',
      criteriaMaxGradeMaxBadge: 'Max: {{max}}',
      criteriaActivityType: 'Activity type',
      criteriaActivityTypePlaceholder: 'Select a type',
      criteriaActivityTypeRequired: 'Activity type is required',
      criteriaSumMismatch:
        'The sum of criteria grades cannot exceed {{expected}}, but it is {{actual}}',
      criteriaSaveFailed_one: 'Criterion of order {{orders}} failed to save',
      criteriaSaveFailed_other: 'Criteria of orders {{orders}} failed to save',
      deleteCriteria: 'Delete criterion',
      createAssessmentItemSuccess: 'Item created successfully',
      updateAssessmentItemSuccess: 'Item updated successfully',
      deleteAssessmentItemSuccess: 'Item deleted successfully',
      deleteAssessmentItem: 'Delete Evaluation Item',
      confirmDeleteAssessmentItem: 'Are you sure you want to delete this item?',
      editAssessment: 'Edit assessment',
      createAssessmentSuccess: 'Evaluation created successfully',
      updateAssessmentSuccess: 'Evaluation updated successfully',
      deleteAssessmentSuccess: 'Evaluation deleted successfully',
      applyTemplateModalTitle: 'Apply template to courses',
      applyTemplateModalDescription:
        'Select a module and add the courses where this block will be applied.',
      selectModuleLabel: 'Module',
      selectModulePlaceholder: 'Select a module',
      appliedCoursePathsLabel: 'Linked courses',
      addCoursePath: 'Add',
      removeCoursePath: 'Remove',
      applyTemplateSyncSuccess: 'Applications updated successfully',
      emptyCoursePathsForModule: 'No courses available in this module',
      clearSelectedUnits: 'Clear selected units',
      coursePath: {
        templateSelectLabel: 'Evaluation Template',
        templateSelectPlaceholder: 'Select a template',
        replaceConfirmTitle: 'Replace evaluation',
        replaceConfirmDescription:
          'There is already an evaluation applied to this course. Do you want to replace it with the new template?',
        applyTemplateSuccess: 'Template applied successfully',
        noEvaluationsCreated:
          'No evaluations created in course {{courseLineName}}',
        editCourseLine: 'Edit course',
      },
    },
    courseWorkspace: {
      title: 'Pedagogical Content',
      subtitle: 'Course Workspace #{{courseId}}',
      newCourse: 'New Pedagogical Content',
      draft: 'Draft',
      published: 'Published',
      publish: 'Publish',
      publishBetaSuccess: 'Pedagogical content published for beta',
      publishSucess: 'Pedagogical content published successfully',
      updateMode: 'Update mode',
      updateModeDescription:
        'In update mode, some features are limited. You can change information, but not the content structure.',
      tabs: {
        information: 'Information',
        units: 'Units',
        evaluations: 'Evaluations',
        exams: 'Exams',
        lessonPlans: 'Lesson plans',
      },
      unitsTab: {
        title: 'Units',
        addUnit: 'Add Unit',
        addLesson: 'Add Lesson',
        emptyState: 'No units registered',
        reorderSuccess: 'Order updated successfully',
        createSuccess: 'Unit created successfully',
        deleteSuccess: 'Unit removed successfully',
        unitName: 'Unit Name',
        dndPlaceholder: 'Create or drag lessons to this unit',
        unitNamePlaceholder: 'Enter the unit name',
        unitNameRequired: 'Unit name is required',
        cancel: 'Cancel',
        save: 'Save',
        addQuestions: 'Questions',
      },
      validation: {
        noPublishedLessons: {
          title: 'No published lessons',
          description:
            'Please create and publish lessons to continue with the publication of the course',
          close: 'Close',
        },
        emptyActivities: {
          title: 'Activities without content',
          description:
            'The following activities have no content and must be filled before publishing:',
          item: 'Activity {{name}} — Lessons: {{lessons}}',
          close: 'Close',
        },
      },
    },
    moduleWorkspace: {
      title: 'Module',
      subtitle: 'Module Workspace #{{moduleId}}',
    },
    tree: {
      newModule: 'New Module',
      newPedContent: 'New Ped. Content',
      newUnit: 'New Unit',
      newLesson: 'New Lesson',
    },
    pedContentTab: {
      title: 'Pedagogical Content',
      addPedContent: 'Add Pedagogical Content',
      deleteSuccess: 'Version discarded successfully',
      createSuccess: 'Pedagogical content created successfully',
      versionSuccess: 'New version created successfully',
      backToVersioningSuccess: 'Pedagogical content moved back to versioning',
      publish: 'Publish',
      publishBeta: 'Publish to Beta',
      versioning: 'Version',
      noVersioningLessons: 'No lessons being edited to publish',
      publishLessons: 'Publish changed lessons',
      publishLessonsDescription:
        'Choose which lessons you want to release now. Changes to the selected lessons become visible to the classes as soon as they are published. The others stay saved in editing so you can publish them later.',
      selectAll: 'Select all',
      saveAndClose: 'Save and Close',
    },
    courseForm: {
      form: {
        name: 'Pedagogical Content Name',
        namePlaceholder: 'Enter the pedagogical content name',
        nameRequired: 'Name is required',
        abbreviation: 'Abbreviation',
        abbreviationPlaceholder: 'Enter the abbreviation',
        abbreviationRequired: 'Abbreviation is required',
        lessonsQuantity: 'Number of Lessons',
        lessonsQuantityPlaceholder: 'Enter the number of lessons',
        lessonsQuantityRequired: 'Number of lessons is required',
        lessonsQuantityMin: 'Number of lessons must be at least {{min}}',
        format: 'Format',
        formatRequired: 'Select at least one format',
        businessModel: 'Business Model',
        businessModelRequired: 'Select at least one business model',
        save: 'Save',
        cancel: 'Cancel',
      },
      alerts: {
        createSuccess: 'Pedagogical content created successfully',
        updateSuccess: 'Pedagogical content updated successfully',
      },
    },
    modulesTab: {
      title: 'Modules',
      addModule: 'Add Module',
      saveOrder: 'Save Order',
      addPedContent: 'Pedagogical Content',
      emptyState: 'No modules registered',
      deleteConfirm: 'Do you want to delete the module "{{name}}"?',
      deleteSuccess: 'Module deleted successfully',
      createSuccess: 'Module created successfully',
      reorderSuccess: 'Module order updated successfully',
    },
    moduleForm: {
      form: {
        moduleName: 'Module Name',
        moduleNamePlaceholder: 'Enter the module name',
        moduleNameRequired: 'Module name is required',
        moduleCover: 'Module Cover',
        save: 'Save',
        cancel: 'Cancel',
      },
      workspace: {
        title: 'Module',
        newModule: 'New Module',
        tabs: {
          information: 'Information',
          contents: 'Pedagogical content',
        },
      },
      alerts: {
        createSuccess: 'Module created successfully',
        updateSuccess: 'Module updated successfully',
      },
    },
    unitForm: {
      form: {
        unitName: 'Unit Name',
        unitNamePlaceholder: 'Enter the unit name',
        unitNameRequired: 'Unit name is required',
        save: 'Save',
      },
      workspace: {
        title: 'Units',
        newUnit: 'New Unit',
        tabs: {
          information: 'Information',
          lessons: 'Lessons',
          flashcards: 'Flashcards',
        },
        flashcardsError: {
          title: 'Error loading flashcards',
          description:
            'An error occurred while loading flashcards. Please, try again.',
          retryBtn: 'Retry',
        },
      },
      alerts: {
        createSuccess: 'Unit created successfully',
        updateSuccess: 'Unit updated successfully',
      },
    },
    lessonForm: {
      form: {
        lessonName: 'Lesson Name',
        lessonNamePlaceholder: 'Enter the lesson name',
        lessonNameRequired: 'Lesson name is required',
        lessonDescription: 'Lesson Description',
        lessonDescriptionPlaceholder: 'Enter the lesson description',
        lessonObjectives: 'Lesson Objectives',
        lessonObjectivesPlaceholder: 'Enter the lesson objectives',
        save: 'Save',
        cancel: 'Cancel',
      },
      workspace: {
        title: 'Lessons',
        newLesson: 'New Lesson',
        tabs: {
          information: 'Information',
          questions: 'Questions',
          resources: 'Resources',
          materials: 'Materials',
          challenge: 'Challenge',
          materialsSubTabs: {
            student: 'Student',
            teacher: 'Teacher',
            noCoursePathFallback: 'Fail to load the pedagogical content',
          },
        },
      },
      alerts: {
        createSuccess: 'Lesson created successfully',
        updateSuccess: 'Lesson updated successfully',
      },
    },
    lessonsTab: {
      title: 'Lessons',
      addLesson: 'Add Lesson',
      createSuccess: 'Lesson created successfully',
      deleteSuccess: 'Lesson deleted successfully',
      reorderSuccess: 'Order updated successfully',
      resourcesShortcut: {
        title: 'Resources',
        openLabel: 'Open unit resources',
      },
    },
    examsTab: {
      createExam: 'Create Exam',
      createSuccess: 'Exam created successfully',
      updateSuccess: 'Exam updated successfully',
      deleteSuccess: 'Exam deleted successfully',
      reorderSuccess: 'Exam order updated successfully',
    },
    examWorkspace: {
      title: 'Exams',
      newExam: 'New exam',
      tabs: {
        information: 'Information',
        questions: 'Exam questions',
        settings: 'Settings',
      },
      settings: {
        defaultTimeLabel: 'Maximum exam time',
        defaultTimePlaceholder: '-- min',
        defaultTimeRequired: 'Maximum time is required',
        defaultTimeMin: 'Maximum time must be greater than zero',
        suggestions: 'Suggestions',
        minutes_one: '{{count}} min',
        minutes_other: '{{count}} min',
        save: 'Save',
        areasTitle: 'Exam blocks order',
        areasInfo:
          'Drag and order the blocks below to set the order students will take the exam activities.',
        noAreasFallback: 'No areas created for this course category',
        showLabel: 'Show?',
        attachSuccess: 'Block added to exam',
        detachSuccess: 'Block removed from exam',
        reorderSuccess: 'Blocks order updated',
        detachConfirmTitle: 'Remove area from exam?',
        detachConfirmDescription_one:
          'This area has 1 linked activity that will be unlinked. Continue?',
        detachConfirmDescription_other:
          'This area has {{count}} linked activities that will be unlinked. Continue?',
        detachConfirmLabel: 'Remove',
      },
      questions: {
        dndPlaceholder: 'Drag and drop question blocks here to add to the exam',
        createBlock: 'Create Question Block',
        createQuestion: 'Create question',
        blockName: 'Question Block name',
        blockNamePlaceholder: 'Ex: Block 1',
        blockNameRequired: 'Block name is required',
        area: 'Skill',
        areaPlaceholder: 'Select skill',
        areaRequired: 'Skill is required',
        hasTime: 'Has time?',
        timeLimitLabel: 'Max time for this block',
        timeLimitPlaceholder: '-- min',
        timeLimitMax: 'Max: {{count}}',
        timeLimitRequired: 'Enter the max time',
        timeLimitMaxError: 'Maximum allowed time is {{count}} minutes',
        randomize: 'Randomize?',
        randomizeInfo:
          "By enabling 'Randomize', this block's questions are shuffled per student, so exams are never repeated.",
        shuffleEnabled: 'Randomization enabled',
        createSuccess: 'Block created successfully',
        updateSuccess: 'Block updated',
        deleteSuccess: 'Block deleted',
        noAreasFallback: 'Configure exam areas in Settings first',
        goToSettings: 'Go to Settings',
        selectFilterToViewBlocks: 'Select a filter above to view blocks',
        allAreas: 'All',
        save: 'Save',
        cancel: 'Cancel',
        pointsCount_one: '{{count}} point',
        pointsCount_other: '{{count}} points',
        reorderQuestionsSuccess: 'Questions order updated successfully',
      },
    },
    examForm: {
      form: {
        name: 'Exam name',
        namePlaceholder: 'Enter exam name',
        nameRequired: 'Exam name is required',
        description: 'Exam description',
        descriptionPlaceholder: 'Briefly describe this exam',
        type: 'Exam type',
        typeRequired: 'Exam type is required',
        evaluation: 'Evaluation block',
        evaluationPlaceholder: 'Select block',
        evaluationRequired: 'Evaluation block is required',
        assessment: 'Assessment',
        assessmentPlaceholder: 'Select assessment',
        assessmentRequired: 'Assessment is required',
        assessmentItem: 'Assessment item',
        assessmentItemPlaceholder: 'Select item',
        assessmentItemRequired: 'Assessment item is required',
        assessmentItemAlreadyInUse: 'This item is already used by another exam',
        simulatedNoAssessmentInfo:
          'Simulated exams are not graded and are not connected to the course evaluation system. They serve only as a tool to help students prepare better.',
        save: 'Save',
        cancel: 'Cancel',
      },
      alerts: {
        createSuccess: 'Exam created successfully',
        updateSuccess: 'Exam updated successfully',
      },
    },
    resourcesTab: {
      createResource: 'Add resource',
      deleteResource: 'Delete resource',
      viewResource: 'View resource',
      downloadResource: 'Download resource',
      createSuccess: 'Resource created successfully',
      updateSuccess: 'Resource updated successfully',
      deleteSuccess: 'Resource deleted successfully',
      reorderSuccess: 'Resource order updated successfully',
      mandatoryBadge: 'Mandatory',
      onlyTeacherBadge: 'Teacher only',
    },
    materialsTab: {
      addMaterial: 'Add material',
      typeSelector: {
        title: 'What kind of material do you want to add?',
        description:
          'Choose an option. The next form changes according to the chosen type.',
        continue: 'Continue',
        options: {
          TEXT: {
            name: 'Text',
            description:
              'Write the content in the editor, with formatting, links and images.',
          },
          SLIDE: {
            name: 'Presentation',
            description:
              'Build the material in slides, with interactive blocks.',
          },
          PDF_INTERACTIVE: {
            name: 'PDF',
            description: 'Upload a ready .pdf file, up to 100 MB.',
          },
        },
      },
      card: {
        names: {
          TEXT: 'Text Material',
          SLIDE: 'Presentation Material',
          PDF_INTERACTIVE: 'PDF Material',
        },
        preview: 'Preview material',
        deleteConfirmTitle: 'Delete material?',
        deleteConfirmDescription:
          'The material and all of its content will be permanently removed from this lesson. To have a material here again, you will need to create a new one.',
      },
      singleMaterialWarning: {
        title: 'Attention',
        description:
          'Currently, a lesson cannot have more than one type of material. To use another type (Text, Slides or PDF), delete the current material and create a new one.',
      },
      textEditor: {
        title: 'Text Material',
        materials: 'Materials',
        textMaterial: 'Text material',
        preview: 'Preview',
        finish: 'Finish',
        structure: 'Structure',
        newChapter: 'New chapter',
        chapter: 'Chapter {{number}}',
        chapterTitle: 'Title',
        autosaved: 'Saved automatically',
        saving: 'Saving...',
        saveError: 'Failed to save',
        deleteChapter: 'Delete chapter',
        deleteChapterDescription:
          'The chapter "{{title}}" will be deleted from the material. This action cannot be undone.',
        lastChapter: 'The material must have at least one chapter',
        previewTitle: 'Preview',
        backToEditor: 'Back to editor',
        viewAs: 'View as',
        viewAsStudent: 'Student',
        viewAsTeacher: 'Teacher',
        desktop: 'Desktop',
        mobile: {
          title: 'Mobile',
          chapterList: 'Mobile · chapter list',
          chapterReader: 'Mobile · chapter reader',
        },
        studentPreview: {
          courseName: 'CNA Teen Up 2',
          ctrlPlayCourseName: 'Ctrl+Young',
          lessonDescription_one: 'Reading material · {{count}} chapter',
          lessonDescription_other: 'Reading material · {{count}} chapters',
        },
      },
      pdfForm: {
        title: 'PDF material',
        titleLabel: 'Title',
        titlePlaceholder: 'Enter the material title',
        titleRequired: 'The title is required',
        dropzoneTitle: 'Drag the PDF here or select a file',
        dropzoneDescription: 'Only .pdf files · up to 100 MB',
        cancelUpload: 'Cancel upload',
        uploadFailed: 'Upload failed. Check your connection and try again.',
        uploadedMeta: '{{size}} · uploaded just now',
        removeFile: 'Remove file',
        viewFile: 'View file',
        removeFileConfirm: {
          title: 'Remove the file from this material?',
          description:
            '“{{name}}” will be removed. To save the material, you will need to upload another PDF.',
        },
        saveSuccess: 'Material saved successfully',
      },
    },
    lessonPlansTab: {
      addLessonPlan: 'Add lesson',
      addLesson: 'Add item',
      name: 'Lesson name',
      namePlaceholder: 'Enter the lesson name',
      nameRequired: 'Name is required',
      cancel: 'Cancel',
      save: 'Save',
      createSuccess: 'Lesson created successfully',
      updateSuccess: 'Lesson updated successfully',
      deleteSuccess: 'Lesson deleted successfully',
      itemName: 'Item name',
      itemNamePlaceholder: 'Enter the item name',
      contentType: 'Content type',
      contentTypeRegular: 'Regular',
      contentTypeEvaluation: 'Evaluation',
      time: 'Time',
      timePlaceholder: '-- min',
      timeRequired: 'Time is required',
      assessment: 'Assessment',
      assessmentPlaceholder: 'Select the assessment',
      assessmentRequired: 'Assessment is required',
      assessmentItem: 'Assessment item',
      assessmentItemPlaceholder: 'Select the assessment item',
      assessmentItemRequired: 'Assessment item is required',
      evaluationBadge: 'Evaluation',
      minutes: '{{time}} min',
      totalLessons: 'Total lessons: {{count}}',
      totalCourseTime: 'Total course time: {{time}}',
      blockTotalTime: 'Total: {{time}}',
      itemCreateSuccess: 'Item created successfully',
      itemUpdateSuccess: 'Item updated successfully',
      itemDeleteSuccess: 'Item deleted successfully',
      reorderSuccess: 'Order updated successfully',
      itemDndPlaceholder: 'Drag items to this block',
    },
    resourceForm: {
      form: {
        title: 'Title',
        titlePlaceholder: 'Enter resource title',
        titleRequired: 'Title is required',
        link: 'Link',
        linkPlaceholder: 'Paste resource link',
        linkRequired: 'Link is required',
        linkInvalid: 'Invalid link',
        file: 'File',
        fileRequired: 'File is required',
        currentFile: 'Current file',
        typeLink: 'Link',
        typeFile: 'Media',
        isMandatory: 'Mandatory',
        allowDownload: 'Allow download',
        onlyTeacher: 'Teacher only',
        save: 'Save',
        cancel: 'Cancel',
        uploadingFile: 'Uploading file, please wait...',
      },
    },
    questionsTab: {
      addBlock: 'Create Question Block',
      addFlashCardBlock: 'Create Flashcard Block',
      createFlashcardQuestion: 'Create Flashcard',
      addQuestion: 'Create question',
      createBlockSuccess: 'Question block created successfully',
      deleteBlockSuccess: 'Question block deleted successfully',
      updateBlockSuccess: 'Question block updated successfully',
      dndPlaceholder: 'Drag and drop questions here to add to the block',
      blockName: 'Block name',
      blockNamePlaceholder: 'Enter block name',
      blockNameRequired: 'Block name is required',
      blockType: 'Block type',
      questionBank: 'Question bank',
      createQuestionPlaceholder: 'Create question',
      active: 'Active',
      inactive: 'Inactive',
      cancel: 'Cancel',
      save: 'Save',
      reorderQuestionsSuccess: 'Questions order updated successfully',
      deleteQuestionSuccess: 'Question deleted successfully',
      createQuestionModal: {
        title: 'Create activity',
        educationalParameter: 'Educational Parameter',
        typology: 'Typology',
        selectPlaceholder: 'Select',
        save: 'Save',
        back: 'back',
        questionCounter: 'Question {{current}} of {{total}}',
        saveSuccess: 'Questions saved successfully',
        incompleteBlockTitle: 'There are incomplete questions in this block!',
        incompleteBlockDescription:
          'To save the question block, finish filling in the items:',
        incompleteBlockFix: 'Fix',
        saveGradeError: 'Failed to save question score',
        resolveQuestionError:
          'Could not find the question in the current version of the question block. Reload the page and try again.',
        newQuestion: 'New question',
        sidebarItem: 'Question {{number}}: {{type}}',
        selectQuestionPlaceholder: 'Select a question on the side',
        blockLabel: 'Block',
        previewPlaceholder: 'Preview will be implemented soon',
        questionForm: {
          title: 'Statement',
          titleRequired: 'The statement is required',
          titleDescription: 'Add the explanation for this question',
          context: 'Context',
          contextDescription: 'Add the explanation for this question',
          contextPlaceholder: 'Write the question context here...',
          contextRequired: 'The content is required',
          formTitle: 'Question instructions',
          contentTab: 'Content',
          feedbackTab: 'Feedback',
          save: 'Save',
          cancel: 'Cancel',
          saveSuccess: 'Question saved successfully',
          updateSuccess: 'Question updated successfully',
          editText: 'Edit text',
          editMedia: 'Edit media',
          markCorrect: 'Mark as correct',
          markIncorrect: 'Mark as incorrect',
          delete: 'Delete',
          altTextPlaceholder: 'Alt text (accessibility)',
          points: 'Score',
          pointsPlaceholder: '0 to 10',
          pointsRange: 'Score must be between 0 and 10',
          pointsRequired: 'Score is required',
          pointsSectionTitle: 'Assign a score to this question',
        },
        answersCommon: {
          answersTitle: 'Answer fields',
          addOption: 'Add option',
          newOption: 'New option',
          correctIn: 'Correct in:',
          validAtBlanks: 'Correct at blanks:',
          optionContentRequired: 'Each option must have content',
          atLeastOneCorrectOption:
            'At least one option must be marked as correct',
          minOptionsRequired_one: 'At least one option must be added',
          minOptionsRequired_other: 'At least {{count}} options must be added',
        },
        quizForm: {
          titlePlaceholder: 'Quiz question',
          optionPlaceholder: 'Option text',
          mediaPlaceholder: 'Select an image',
        },
        trueOrFalseForm: {
          titlePlaceholder: 'Title of the True or False question',
          whatIsTheAnswer: 'What is the answer?',
          true: 'True',
          false: 'False',
        },
        flashcardForm: {
          titlePlaceholder: 'Title of the flashcard question',
          frontContent: 'Front',
          backContent: 'Back (answer)',
          contextDescription:
            'Add the answer to this flashcard (text, image, audio, etc.)',
          showFrontOnBack: 'Visible when flipping the card?',
          labelTitle: 'Flashcard Name',
          titleRequired: 'The flashcard name is required',
          frontContentRequired: 'The front content (question) is required',
          backContentRequired: 'The back content (answer) is required',
          frontContentPlaceholder: 'Write the flashcard question here...',
          backContentPlaceholder: 'Write the flashcard answer here...',
        },
        orderingForm: {
          titlePlaceholder: 'Put the word in the correct order',
          orientation: 'Orientation',
          horizontal: 'Horizontal',
          vertical: 'Vertical',
          positionLabel: 'Position {{number}}',
        },
        combinationForm: {
          titlePlaceholder: 'Organize the answers to match the images',
          mainColumnTitle: 'Main column',
          newItem: 'New item',
          itemLabel: 'Item {{number}}',
          tabText: 'Text',
          tabMedia: 'Media',
          everyQuestionNeedsAnswer_one:
            'Item {{items}} needs at least one valid answer',
          everyQuestionNeedsAnswer_other:
            'Items {{items}} need at least one valid answer',
          notEnoughDistinctAnswers:
            'Not enough answers for every item. Each item needs a distinct answer',
        },
        writingForm: {
          titlePlaceholder: 'Title of the Writing task',
        },
        multimediaForm: {
          titlePlaceholder: 'Title of the Text or Video task',
        },
        fillInTheBlankForm: {
          titlePlaceholder: 'Complete the sentence',
          questionLabel: 'Question',
          questionHint:
            'To add a blank, type ___ (3 underscores). Ex: I want ___ pizza.',
          questionPlaceholder: 'Type the question using ___ to mark the blanks',
          questionRequired: 'The question text is required',
          atLeastOneBlankRequired:
            'Add at least one blank (___) in the question text',
          everyBlankNeedsAnswer_one:
            'Blank {{blanks}} needs at least one valid option',
          everyBlankNeedsAnswer_other:
            'Blanks {{blanks}} need at least one valid option',
          notEnoughDistinctOptions:
            'Not enough options to fill every blank. Each blank needs a distinct option',
          responseFieldsTitle: 'Response fields',
          blankBadge: 'Blank {{number}}',
          blankLabel: 'Blank {{number}}',
          previewLabel: 'Question preview',
        },
        feedbacks: {
          title: 'Question feedback',
          types: {
            POSITIVE: 'Positive',
            NEUTRAL: 'Neutral',
            NEGATIVE: 'Negative',
          },
          descriptions: {
            POSITIVE:
              'This message is shown when the student answers correctly.',
            NEUTRAL:
              'This message is shown when the feedback does not cover the answer.',
            NEGATIVE:
              'This message is shown when the student answers incorrectly.',
          },
          titlePlaceholders: {
            POSITIVE: "Ex: That's correct!",
            NEUTRAL: 'Ex: Almost there!',
            NEGATIVE: 'Ex: Try again.',
          },
          messagePlaceholders: {
            POSITIVE: 'Ex: Great job! You got it right.',
            NEUTRAL: 'Ex: Your answer is partially correct.',
            NEGATIVE: "Ex: That's not quite right. Review and try once more.",
          },
        },
      },
    },
    alerts: {
      createSuccess: 'Course line created successfully',
      updateSuccess: 'Course line updated successfully',
    },
  },
  questionsPreview: {
    blockTitle: 'Block preview',
    questionTitle: 'Question preview',
    pageIndicator: 'Question {{current}} of {{total}}',
    fetchError: 'Error loading the block.',
    previousQuestion: 'Previous question',
    nextQuestion: 'Next question',
  },
  questionView: {
    errorFetchingActivities: 'Error fetching question.',
    comingSoonBadge: 'Coming soon',
    unsupportedType: 'Answers preview coming soon',
    studentAnswer: 'Student answer',
    correct: 'Correct',
    corrected: 'Corrected',
    notCorrected: 'Not corrected',
    notAnswered: 'to do',
    writingCorrection: {
      correct: 'Correct',
      incorrect: 'Incorrect',
      partial: 'Partial',
      adjustGrade: 'Adjust grade',
      gradeInput: 'Grade percentage',
      generalFeedbackPlaceholder:
        'Write the general feedback for the student...',
      useAICorrection: 'Use AI answer',
      sendAnswer: 'Send answer',
      correctionSuccess: 'Correction saved successfully',
      yourCorrection: 'Your correction',
      correctedAt: '<strong>Corrected on:</strong> {{hour}}',
      backToKlass: 'Back to class',
      noComment: 'No comment',
    },
    aiCorrection: {
      title: 'AI Correction',
      manualCorrection: 'Manual correction',
      acceptAnswer: 'Accept answer',
      alertTitle: 'Attention',
      alertMsg:
        "The AI's response will be automatically sent to the student after 3 days.",
    },
    fillInTheBlank: {
      blankLabel: 'Blank {{number}}',
    },
    ordering: {
      sequenceLabel: 'Answer sequence',
    },
    combination: {
      slotLabel: 'Answer for {{cue}}',
    },
    typeAnswer: 'Type your answer here',
    noAnswer: 'No answer provided',
    trueOrFalse: {
      true: 'True',
      false: 'False',
      missed: 'Missed',
    },
    flashcard: {
      front: 'Front',
      back: 'Back',
      flipCard: 'Flip card',
      true: 'Correct',
      false: 'Incorrect',
    },
    dnd: {
      usageInstructions:
        'Press the space bar to lift the item, use the arrow keys to move it, and the space bar again to drop. Press escape to cancel.',
      lifted: 'You have lifted {{item}}.',
      movedToPosition: '{{item}} moved to position {{position}}.',
      movedToBank: '{{item}} moved to the available options.',
      notOverZone: '{{item}} is not over a valid area.',
      dropped: '{{item}} dropped at position {{position}}.',
      returnedToBank: '{{item}} returned to the available options.',
      cancelled:
        'Movement cancelled. {{item}} returned to its starting position.',
      bankLabel: 'Available options',
      slotLabel: 'Answer area',
      dropZoneRoleDescription: 'drop zone',
      itemFallback: 'the item',
    },
  },
  klasses: {
    title: 'Classes',
    summary: 'Summary',
    back: 'back',
    columns: {
      performance: 'Class performance',
      klass: 'Class',
      unit: 'Unit',
      startDate: 'Start',
      endDate: 'End',
      status: 'Status',
      details: 'Details',
    },
    summaryBadges: {
      klasses: 'Classes: {{value}}',
      students: 'Students: {{value}}',
      studentsPerKlass: 'Students per Class (average): {{value}}',
    },
    legend: {
      performance: 'Class performance',
      endingSoon: 'Ending soon',
    },
    filters: {
      searchPlaceholder: 'Class, course, teacher, day...',
    },
    openDetails: 'View details of class {{klass}}',
    noDate: 'No date',
    empty: 'No classes found',
    performance: {
      title: 'Overall performance',
      description: `Based on <bold>{{studentsCount}}</bold> students' progress`,
    },
  },
  students: {
    title: 'Students',
    summary: 'Summary',
    back: 'back',
    columns: {
      performance: 'Student performance',
      student: 'Student',
      email: 'Email',
      klass: 'Class',
      status: 'Student status',
      details: 'Details',
    },
    summaryBadges: {
      students: 'Students: {{value}}',
      studentsPerTeacher: 'Students per teacher (average): {{value}}',
      weekAccesses: 'Total accesses (week): {{value}}',
    },
    legend: {
      performance: 'Student performance',
    },
    filters: {
      searchPlaceholder: 'Student, email, username...',
      studentStatus: 'Student status',
      enrollmentStatus: 'Enrollment status',
      ordering: {
        last_login: 'Last access (oldest)',
        '-last_login': 'Last access (most recent)',
      },
    },
    noKlass: 'No class',
    moreKlasses: '+{{count}}',
    noEmail: 'No email',
    openDetails: 'View details of student {{student}}',
    empty: 'No students found',
  },
  klassDetail: {
    title: 'Class details',
    eyebrow: 'Classes',
    back: 'back',
    switchKlass: {
      label: 'Switch class',
      placeholder: 'Search class...',
    },
    tags: {
      students: 'Students: {{value}}',
    },
    tabs: {
      students: 'Students',
      exams: 'Exams',
      activities: 'Activities',
      lessons: 'Lessons',
      pedagogicalSheet: 'Pedagogical Sheet',
    },
    students: {
      columns: {
        performance: 'Student performance',
        student: 'Student',
        email: 'Email',
        unit: 'Unit',
        details: 'Details',
      },
      legend: {
        performance: 'Student performance',
      },
      filters: {
        searchPlaceholder: 'Name, email...',
      },
      empty: 'No students found',
    },
    lessons: {
      columns: {
        lesson: 'Lesson',
        unit: 'Unit',
        date: 'Date',
        status: 'Status',
        absences: 'Absences',
        actions: 'Actions',
      },
      status: {
        CONCLUDED: 'Concluded',
        PENDING: 'Pending',
        SCHEDULED: 'Scheduled',
      },
      filters: {
        all: 'All',
      },
      teacherName: '(Prof. {{name}})',
      dateEnd: 'to {{time}}',
      absencesCount: '{{absences}} of {{total}}',
      notAvailable: 'N/A',
      viewReport: 'View report',
      empty: 'No lessons found',
    },
    activities: {
      filters: {
        searchPlaceholder: 'Activity, student...',
        student: 'Student',
        status: 'Status',
        periodSend: 'Sent period',
        all: 'All',
      },
      empty: 'No activities found',
    },
    pedagogicalSheet: {
      filters: {
        date: 'Date',
        student: 'Student',
        recordType: 'Record type',
        all: 'All',
      },
      recordTypes: {
        OCCURRENCE: 'Occurrence',
        ASSESSMENT: 'Assessment',
        LESSON_EVENT: 'Lesson events',
      },
      records: {
        lessonStarted: 'Lesson started',
        lessonFinished: 'Lesson finished',
        assessmentDone: 'Assessment done',
      },
      actions: {
        addOccurrence: 'Occurrence',
        addAssessment: 'Assessment',
      },
      forms: {
        newOccurrence: 'New occurrence',
        editOccurrence: 'Edit occurrence',
        newAssessment: 'Assessment',
        editAssessment: 'Edit assessment',
        student: 'Student',
        studentPlaceholder: 'Select the student',
        comment: 'Comment',
        commentPlaceholder: 'Write a comment about the class',
        ratingPlaceholder: 'Select',
        studentRequired: 'The student is required',
        descriptionRequired: 'The description is required',
        energyRequired: 'The energy is required',
        speedRequired: 'The speed is required',
        maturityRequired: 'The maturity is required',
        sheetNotFound: 'Student pedagogical sheet not found',
      },
      empty: 'No records found in the period',
    },
    exams: {
      columns: {
        exam: 'Exam',
        student: 'Student',
        status: 'Status',
        deliveredAt: 'Delivered at',
        grade: 'Grade',
        actions: 'Actions',
      },
      status: {
        PENDING: 'To Correct',
        CORRECTED: 'Corrected',
      },
      filters: {
        searchPlaceholder: 'Exam, student...',
        exam: 'Exam',
        allExams: 'All',
        status: 'Status',
        allStatuses: 'All',
        periodDelivered: 'Delivered period',
      },
      legend: {
        requiresAttention: 'Requires attention',
      },
      gradeNotAvailable: 'N/A',
      empty: 'No exams found',
    },
  },
  studentDetail: {
    title: 'Student details',
    eyebrow: 'Student',
    back: 'back',
    switchCourse: {
      label: 'Switch course',
      current: '{{course}} (current)',
      closed: '{{course}} (closed)',
      empty: 'No course found',
    },
    tags: {
      age: 'Age: {{count}} years',
      noKlass: 'No class',
    },
    tabs: {
      summary: 'Summary',
      activities: 'Activities',
      exams: 'Exams',
      historic: 'History',
      pedagogicalSheet: 'Pedagogical sheet',
      finalGrades: 'Final grades',
    },
    performance: {
      title: 'Student performance',
      description: 'Based on attendance and course progress',
    },
    stats: {
      averageGrade: 'Average Grade',
      averageGradeDescription: '{{graded}} of {{total}} assessments',
      completedLessons: 'Completed Lessons',
      completedLessonsDescription: 'Total: {{total}}',
      attendance: 'Attendance',
      attendanceDescription: 'Absences: {{count}}',
      notAvailable: '-',
    },
    courseStatusAlert: {
      title: 'Attention',
      description:
        'The following information is about courses that are closed or not yet started by the student and is for consultation only. Contact the office if you need to correct exams or activities.',
    },
    summary: {
      lessonsHistory: {
        closedTitle: 'Course history',
        title: 'Lesson history',
        filters: {
          period: 'Lessons period',
          all: 'All',
        },
        empty: 'No lessons found in the period',
      },
      courseProgress: {
        title: 'Course progress',
        pendingTitle: 'Pending lessons',
        seeMore: 'See more',
        empty: 'No pending lessons',
      },
      pendingExams: {
        title: 'Exams to grade',
        seeMore: 'See more',
        empty: 'No pending exams',
      },
      occurrences: {
        title: 'Occurrences',
        filters: {
          period: 'Occurrences period',
          all: 'All',
        },
        empty: 'No occurrences found in the period',
      },
    },
    historic: {
      title: 'Student history',
      columns: {
        date: 'Date',
        item: 'Lesson',
        unit: 'Unit',
        status: 'Status',
      },
      filters: {
        period: 'Period',
      },
      enrollment: {
        ACTIVE: 'Enrolled in class',
        TRANSFERRED: 'Enrollment transferred from class',
        CANCELED: 'Enrollment canceled in class',
      },
      empty: 'No events found in the period',
    },
    exams: {
      title: 'Exams',
      status: {
        PENDING: 'To correct',
        CORRECTED: 'Corrected',
        NOT_SCHEDULED: 'To schedule',
        SCHEDULED: 'Scheduled',
      },
      deliveredAt: 'Delivered on: {{date}}',
      scheduledFor: 'Scheduled for: {{date}}',
      undefinedDate: 'Date: to be defined',
      empty: 'No exams found',
    },
    pedagogicalSheet: {
      title: 'Pedagogical Sheet',
      filters: {
        period: 'Period',
      },
      records: {
        assessmentDone: 'Assessment done',
      },
      actions: {
        addOccurrence: 'Occurrence',
        addAssessment: 'Assessment',
      },
      forms: {
        newAssessment: 'Assessment',
        editAssessment: 'Edit assessment',
        klass: 'Class',
        klassPlaceholder: 'Select a class',
        comment: 'Comment',
        commentPlaceholder: 'Write a comment about the student',
        ratingPlaceholder: 'Select',
        attentionRequired: 'The attention is required',
        apprenticeshipRequired: 'The apprenticeship is required',
        initiativeRequired: 'The interest/initiative is required',
        socializationRequired: 'The socialization is required',
        organizationRequired: 'The organization is required',
        sheetNotFound: 'Student pedagogical sheet not found',
      },
      empty: 'No records found in the period',
    },
  },
  pendencies: {
    dashboardTitle: 'My Pendings',
    title: 'Pending',
    back: 'back',
    allKlasses: 'All classes',
    tabs: {
      attendance: 'Attendance',
      exams: 'Exams',
      activities: 'Activities',
    },
    columns: {
      lesson: 'Lesson',
      unit: 'Unit',
      klass: 'Class',
      date: 'Date',
      actions: 'Actions',
    },
    correction: {
      delivered: 'Delivered: ',
      corrected: 'corrected',
      pending: 'pending',
      save: 'Save',
      cancel: 'Cancel',
      grade: 'Grade',
      correctedSingular: 'Corrected: ',
    },
    viewReport: 'View report',
    outstanding: 'Outstanding',
    empty: 'No pending items found',
    viewAll: 'View all',
    filters: {
      all: 'All',
      period: 'Period',
      searchPlaceholder: 'Lesson, order, type...',
    },
    exams: {
      columns: {
        exam: 'Exam',
        klass: 'Class',
        student: 'Student',
        deliveredAt: 'Delivered at',
        actions: 'Actions',
      },
      correct: 'Correct',
      filters: {
        exam: 'Exam',
        periodDelivered: 'Delivered period',
        searchPlaceholder: 'Exam, student, class...',
      },
    },
    activities: {
      columns: {
        activity: 'Activity',
        lesson: 'Lesson',
        unit: 'Unit',
        klass: 'Class',
        student: 'Student',
        correctionStatus: 'Status',
        sentAt: 'Sent at:',
        status: 'Status',
        actions: 'Actions',
      },
      correct: 'Correct',
      status: 'Status',
      correctionStatus: {
        NOT_SUBMITTED: 'Not submitted',
        PENDING: 'Not corrected',
        CORRECTED: 'Corrected',
      },
      filters: {
        student: 'Student',
        periodSend: 'Send period',
        searchPlaceholder: 'Activity, student...',
      },
    },
  },
  examScheduling: {
    title: 'Scheduling',
    headerLabel: 'Exams',
    back: 'back',
    scheduleExams: 'Schedule exams',
    availableExams: {
      title: 'Available exams',
      subtitle: 'Select an exam to schedule it.',
    },
    filters: {
      searchPlaceholder: 'Exam, class...',
      all: 'All',
      status: {
        label: 'Status',
        notScheduled: 'Not scheduled',
        scheduled: 'Scheduled',
      },
    },
    card: {
      notScheduled: 'Not scheduled',
      scheduled: 'Scheduled',
      expectedFor: 'Expected for: {{date}}',
      scheduledFor: 'Scheduled for: {{date}}',
    },
    empty: 'No exams found',
    wizard: {
      datetime: {
        title: 'Date and Time',
        subtitle: 'Select the period the exam will be available',
        startTime: 'Starts on {{startDate}} at:',
        endTime: 'Ends on {{endDate}} at:',
      },
      summary: {
        title: 'Summary',
        subtitle: 'Check the information below to schedule the exam',
        period: '{{startDate}} at {{startTime}} to {{endDate}} at {{endTime}}',
        exam: 'Exam',
        dates: 'Dates and times',
        duration: 'Duration',
        durationValue: '{{minutes}} minutes',
        editExam: 'Edit exam',
        editDates: 'Edit dates and times',
      },
      success: {
        title: 'Successfully scheduled!',
        assessmentItem: 'Assessment item',
        klasses: 'Class(es)',
        backToHome: 'Back to home',
        otherExam: 'Schedule another exam',
      },
      actions: {
        cancel: 'Cancel',
        continue: 'Continue',
        schedule: 'Schedule',
      },
      errors: {
        invalidTime: 'Invalid time',
        invalidPeriod:
          'Select a valid period: the start date and time must be before the end ones.',
      },
    },
  },
  examCorrection: {
    back: 'back',
    delivered: 'Delivered: ',
    corrected: 'Corrected: ',
    questionCounter: 'Question {{current}} of {{total}}',
    notGraded: '--',
    grade: 'Grade',
    gradeValue: '{{grade}}/{{maxGrade}} ({{percent}}%)',
    gradeUnavailable: '--/{{maxGrade}}',
    empty: {
      title: 'Nothing to correct in this exam',
      description:
        'The exam is marked as delivered, but there are no recorded answers for any skill. Check the student submission before publishing a grade.',
    },
    generalFeedback: 'General Exam Feedback',
    generalFeedbackPlaceholder: 'Write the general feedback for the student...',
    questionFeedback: 'Question feedback',
    questionFeedbackPlaceholder: 'Add feedback for this question',
    saveDraft: 'Save draft',
    publishGrade: 'Publish grade',
    previousBlock: 'Previous',
    nextBlock: 'Next block',
    draftSaved: 'Draft saved successfully',
    publishSuccess: 'Grade published successfully',
    publishConfirm: {
      title: 'Publish the exam grade?',
      description:
        'The grade and feedbacks will become visible to the student. You will still be able to review the correction later.',
      confirmLabel: 'Publish grade',
      cancelLabel: 'Cancel',
    },
    success: {
      grade: 'Grade: {{grade}}/{{maxGrade}} ({{percent}}%)',
    },
  },
  breadcrumb: {
    label: 'Page path',
    home: 'Home',
    myCourses: 'My Courses',
    klasses: 'Classes',
    students: 'Students',
    exams: 'Exams',
    activities: 'Activities',
    scheduling: 'Scheduling',
  },
  filterBar: {
    search: 'Search',
    searchPlaceholder: 'Search..',
    moreFilters: 'More filters',
    lessFilters: 'Fewer filters',
    clearAll: 'Clear all',
    removeFilter: 'Remove {{label}} filter',
    results: '{{shown}} of {{count}} results',
  },
  searchSelect: {
    placeholder: 'Select',
    emptyMessage: 'No results',
  },
  select: {
    removeOption: 'Remove {{label}}',
  },
  appShell: {
    nav: {
      label: 'Main navigation',
      home: 'Home',
      lessons: 'Lessons',
      exams: 'Exams',
      courses: 'Courses',
      pendencies: 'Pendencies',
      students: 'Students',
      klasses: 'Classes',
    },
    sidebar: {
      expand: 'Expand menu',
      collapse: 'Collapse menu',
      logout: 'Log out',
    },
    account: {
      label: 'Account menu',
      coins: 'CNA Coins',
      gems: 'CNA Gems',
      settings: 'Settings',
      close: 'Close account menu',
    },
    openAccountMenu: 'Open account menu',
  },
};

export default en;
